import {
    Championships, CompanyLogs,
    db,
    Events,
    Feuds,
    MatchContracts,
    Matches,
    NewsItems,
    Relationships,
    Segments,
    TalentRequests
} from "../models/models.js";
import {
    getArrayMinByProperty,
    getCurrentGame,
    getRandomChoice,
    getRandomInt,
    getRandomSample,
    getRosterMemberAlignment,
    getRosterMemberBrandName,
    getRosterMemberPopularity,
    getUniqueObjects,
    limitValueBetweenRange,
    objectInArray,
    weightedChoice,
    commaSeparate, asyncFor, gameLimits, captureError
} from "./helpers.js";
import {
    getGameActiveCompanies,
    getGameCurrentUserCompany,
    getGameMainUserCompany,
    getUserControlledCompanies, saveGameDataToLocalStorage
} from "./games.js";
import {
    addFormerCompanyRelationship,
    changeProperty,
    generateGimmickMatchLocationName,
    generateGimmickMatchObjectName,
    getActiveCompetitorsForCompany,
    getAvailableStaffMembers,
    getAvailableStaffMembersByRole,
    getChampionshipCost,
    getChampionshipPopularityDecrease,
    getCompanyLatestEvent, getCompanyMonth,
    getCompanyParentEvents,
    getCompanyRosterByType,
    getCompanyRosterCapacity, getCompanyYear,
    getCpuControlledCompanies, getCurrentMonth,
    getViewershipData,
    processObjectiveCompleted,
    processSuperstarsYearlyChanges, reviewObjectives
} from "./companies.js";
import {
    addRosterFactions,
    addRosterFeuds,
    addRosterSuperstars,
    addRosterTagTeams,
    getRosterBrands,
    getRosterFeuds,
    getRosterSuperstars,
    getRosterTagTeams
} from "../models/roster.js";
import {
    addCompanyChampionships,
    addCompanyCompanyLogs,
    addCompanyEvents, addCompanyMessages,
    addCompanyNewsItems,
    getCompanyChampionships,
    getCompanyCompanyLogs,
    getCompanyEvents,
    getCompanyFacilities,
    getCompanyFeudItems,
    getCompanyGimmickMatches,
    getCompanyNewsItems, getCompanyObjectives,
    getCompanyPowerups,
    getCompanyRosters,
    getCompanySegmentItems,
    getCompanySentNegotiations,
    getCompanyStaff,
    removeCompanyChampionships
} from "../models/company.js";
import {
    addWeeklyShow,
    getEventHype,
    getEventMatchesAndSegments,
    getEventMatchesData,
    getEventMaximumMatchesAndSegments,
    getEventMinimumMatchesAndSegments,
    getEventPosterGraphicData,
    getEventSegmentsData,
    getParentEventForEvent,
    getTopEvent, getUpcomingMonthlyEventForWeeklyEvent, getWeeklyEventName, playEvent
} from "./events.js";
import {deleteMatch, getHypeForMatch, getSuperstarsBookedAtEventMatches, simulateMatchWinner} from "./matches.js";
import {
    checkSegmentItemRequirement,
    deleteSegment,
    getHypeForSegment,
    getSegmentConsequenceAppliedTo,
    introduceSpecialSegments,
    processPlaySegment
} from "./segments.js";
import {
    addSuperstarsToManager,
    changeSuperstarMoraleToHappy,
    checkAvailability,
    checkSuperstarBrandAvailability,
    getRankFromPopularity,
    getRosterMemberSuperstars,
    getSuperstarChampionshipsFromCompanyChampionships,
    getSuperstarCharisma,
    getSuperstarWorkrate,
    removeSuperstarFactionRelationships,
    removeSuperstarManagerRelationships,
    removeSuperstarsFromManager,
    removeSuperstarTagTeams,
    updateSuperstarMorale
} from "./superstars.js";
import {getTagTeamSuperstars} from "../models/tagTeam.js";
import {
    addChildEvent,
    addEventMatches,
    addEventSegments,
    Event,
    getEventMatchContracts,
    getEventMatches, getEventSegments, getParentEvent
} from "../models/event.js";
import {
    addMatchChampionships,
    addMatchFeuds,
    addMatchParticipants,
    getMatchGimmickMatch,
    getMatchParticipants,
    Match
} from "../models/match.js";
import {addFeudSuperstars, addFeudTagTeams, Feud, getFeudFeudItem, getFeudParticipants} from "../models/feud.js";
import {
    getSuperstarCurrentGimmick, getSuperstarManager,
    getSuperstarRelationships,
    getSuperstarTagTeams,
    getSuperstarTalentRequests,
    removeSuperstarChampionships
} from "../models/superstar.js";
import {getMatchContractTalent} from "../models/matchContract.js";
import {
    checkAreChampionshipsEqual,
    checkChampionshipGimmickRestriction,
    getUniqueChampionships
} from "./championships.js";
import {addGimmickMatchMatches} from "../models/gimmickMatch.js";
import {
    generateRandomFeudItemName,
    getAvailableFeudItems,
    getFeudConsequenceAppliedTo,
    getFeudCreationPreviousFeudsBoost,
    getFeudRating,
    getMatchesForStaleFeud,
    getParticipantFeuds,
    getRosterMemberFeuds
} from "./feuds.js";
import {globalUser} from "./globals.js";
import {setManagersForMatchOrSegment} from "./managers.js";
import {addFeudItemFeuds} from "../models/feudItem.js";
import {addSegmentFeuds, addSegmentParticipants, Segment} from "../models/segment.js";
import {addSegmentItemSegments} from "../models/segmentItem.js";
import {
    generateChampionshipGraphic,
    generateChampionshipName,
    generateFaction, generateGimmickName,
    generateSuperstar,
    generateTagTeam,
    generateTrio
} from "./generate.js";
import {draftSuperstarToBrand, getBrandIndexForWeeklyShow} from "./brands.js";
import {formatCompanyNameBackend} from "../ui/ui-helpers.js";
import {endEvent} from "./endEvent.js";
import {processPlayMatch} from "./processMatch.js";
import {matchFinishesData} from "../data/matchFinishes.js";
import {applyPowerupToSelection, getPowerupSelections, introducePowerups} from "./powerups.js";
import {processAwards} from "./awards.js";
import {
    callupTalentToMainRoster,
    checkCharacterUsage,
    createGimmick,
    getGimmickCreationBonus,
    transferTalentToRoster
} from "./rosters.js";
import {getTagTeamPopularity, removeTagTeam} from "./tagTeams.js";
import {checkTalentRequestAction} from "./talentRequests.js";
import {generateNewsItem} from "./news.js";
import {getTalentValueAmount, makeTalentOffer} from "./negotiations.js";
import {
    Championship,
    getChampionshipCurrentReign,
    getChampionshipMatchContract,
    getChampionshipSuperstar
} from "../models/championship.js";
import {
    disbandFaction,
    getRosterActiveFactions,
    processFactionXpChangeForFaction,
    removeSuperstarsFromFaction
} from "./factions.js";
import {addFactionSuperstars, getFactionSuperstars, setFactionLeaderSuperstar} from "../models/faction.js";
import {CompanyLog} from "../models/companyLog.js";
import {charactersData} from "../data/characters.js";
import {personalitiesData} from "../data/personalities.js";
import {processYearlyRanking} from "./rankings.js";
import {loadUser} from "./user.js";
import {checkToAddCurrentObjectivesMessage} from "./messages.js";

async function autoBookEventRequest({style, numberOption, addOption, hideBooked} = {}) {
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const latestEvent = await getCompanyLatestEvent(userCompany);
    const staffMembers = await getCompanyStaff(userCompany);
    const producers = getAvailableStaffMembersByRole(staffMembers, 'producer');

    // Get cost
    const costsData = {'standard': 10, 'long-term': 20, 'quality': 15, 'bizarro': 5, 'terrible': 2};
    let cost = costsData[style] || 10;
    if (latestEvent.weeklyShow) {
        cost = Math.floor(cost / 2)
    }

    // Check points
    if (userCompany.points < cost) {
        data['error'] = 'points';
        return data;
    }

    // Don't allow at premier event in POW
    if (userCompany.storyName === 'passion' && addOption === 'replace') {
        const sponsorGoal = (await getCompanyObjectives(userCompany)).find(x => x.name === 'Get some Sponsors');
        if (!sponsorGoal.completed && !sponsorGoal.failed) {
            data['error'] = 'pow-cannot-replace';
            return data;
        }
    }

    // Auto book event
    await autoBookEvent({company: userCompany, roster: mainRoster, rosterSuperstars, latestEvent, producers,
        game: currentGame, style, numberOption, addOption, hideBooked});

    // Subtract points
    userCompany.points -= cost;

    db.saveDatabase();

    // Get data to return
    data['points'] = userCompany.points;
    const matches = await getEventMatchesData({event: latestEvent, rosterSuperstars: rosterSuperstars});
    const segments = await getEventSegmentsData({event: latestEvent, rosterSuperstars: rosterSuperstars,
        producers: producers});
    data['matchesAndSegments'] = matches.concat(segments);
    data['matchesAndSegments'].sort((a, b) => a.order - b.order);

    return data;
}

async function autoBookEvent({company, roster, rosterSuperstars, latestEvent, producers, game, style,
                             numberOption = 'auto', addOption = 'replace', hideBooked = false, championships = null,
                             parentEvents = null, companyEvents = null} = {}) {
    let data = {};
    const eventMatchesAndSegments = await getEventMatchesAndSegments(latestEvent);
    const numberOfMatchesAndSegmentsBooked = eventMatchesAndSegments.length;
    const rosterTeams = await getRosterTagTeams(roster);
    const tagTeams = rosterTeams.filter(x => !x.trio);
    const trios = rosterTeams.filter(x => x.trio);
    const gimmickMatches = await getCompanyGimmickMatches(company);
    const feudItems = await getCompanyFeudItems(company);
    const companyLogs = await getCompanyCompanyLogs(company);
    const productionFacility = (await getCompanyFacilities(company)).find(x => x.name === 'Production');
    if (!parentEvents) {
        parentEvents = await getCompanyParentEvents(company);
    }
    if (!championships) {
        championships = await getCompanyChampionships(company);
    }
    if (!companyEvents) {
        companyEvents = (await getCompanyEvents(company)).reverse();
    }

    // Don't use already booked superstars if add option selected
    let bookedSuperstars = [];
    if (addOption === 'add') {
        bookedSuperstars = await getSuperstarsBookedAtEventMatches(latestEvent);
    }

    // If 'replace' option selected, delete matches and segments
    if (addOption === 'replace') {
        for (const x of eventMatchesAndSegments) {
            if (x.objectName === 'segment') {
                await deleteSegment(x, producers)
            }
            else {
                await deleteMatch(x, company)
            }
        }
        latestEvent.matchesCount = 0;
    }

    // Get available
    let availableSuperstars = [];
    for (const x of rosterSuperstars) {
        if (await checkAutoBookParticipantAvailability(x, rosterSuperstars, latestEvent, championships, bookedSuperstars)) {
            availableSuperstars.push(x)
        }
    }
    let availableTagTeams = [];
    for (const x of tagTeams) {
        if (await checkAutoBookParticipantAvailability(x, rosterSuperstars, latestEvent, championships, bookedSuperstars)) {
            availableTagTeams.push(x)
        }
    }
    let availableTrios = [];
    for (const x of trios) {
        if (await checkAutoBookParticipantAvailability(x, rosterSuperstars, latestEvent, championships, bookedSuperstars)) {
            availableTrios.push(x)
        }
    }

    // Fix low number of available superstars for competitors
    if (!company.userControlled) {
        if (availableSuperstars.length < 7) {
            // Generate superstars
            for (const x of Array(5).keys()) {
                let newSuperstar = await generateSuperstar({notManager: true});
                await addRosterSuperstars(roster, [newSuperstar]);
                rosterSuperstars.push(newSuperstar);
                availableSuperstars.push(newSuperstar);
            }
        }
    }

    // Get past event's matches
    let monthlyEvents = companyEvents.filter(x => !x.weeklyShow);
    let pastEventMatches = [];
    for (const m of (await getEventMatches(monthlyEvents[1]))) {
        let matchParticipants = await getMatchParticipants(m);
        let available = true;
        for (const x of matchParticipants) {
            if (!(await checkAutoBookParticipantAvailability(x, rosterSuperstars, latestEvent, championships, bookedSuperstars))) {
                available = false;
            }
        }
        if (matchParticipants.length > 1 && available) {
            pastEventMatches.push(m)
        }
    }

    // Get number to book
    let numberToBook = getEventMatchesAndSegmentsToAutoBook(latestEvent, parentEvents, rosterSuperstars,
        productionFacility, numberOption, style);

    // If 'add' option selected, subtract added segments
    if (addOption === 'add') {
        numberToBook -= numberOfMatchesAndSegmentsBooked;
        if (numberToBook <= 0) {
            data['error'] = 'already-booked';
            return data;
        }
    }

    // Get feuds
    let singlesFeuds = [];
    let tagFeuds = [];
    for (const f of (await getRosterFeuds(roster))) {
        if (f.startMonth !== null && !f.ended) {
            let feudCheckResult = await checkAutoBookFeudAvailability(f, rosterSuperstars, latestEvent, championships, bookedSuperstars);
            if (feudCheckResult[0]) {
                if (feudCheckResult[1] === 'superstar') {
                    singlesFeuds.push(f)
                }
                else if (feudCheckResult[1] === 'tagTeam') {
                    tagFeuds.push(f)
                }
            }
        }
    }

    // Auto book event
    if (latestEvent.weeklyShow || latestEvent.name === 'Weekly Show') {
        data = await autoBookWeeklyEvent(company, roster, availableSuperstars, availableTagTeams, availableTrios,
            championships, style, gimmickMatches, latestEvent, parentEvents, numberToBook, singlesFeuds, tagFeuds,
            pastEventMatches, feudItems, companyLogs, rosterSuperstars, game, hideBooked)
    }
    else {
        data = await autoBookMonthlyEvent(company, roster, availableSuperstars, availableTagTeams, availableTrios,
            championships, style, gimmickMatches, latestEvent, parentEvents, numberToBook, singlesFeuds, tagFeuds,
            pastEventMatches, feudItems, companyLogs, rosterSuperstars, game, hideBooked)
    }

    return data;
}

async function autoBookMonthlyEvent(company, roster, availableSuperstars, availableTagTeams, availableTrios,
                                    championships, style, gimmickMatches, latestEvent, parentEvents, numberToBook,
                                    singlesFeuds, tagFeuds, pastEventMatches, feudItems, companyLogs, rosterSuperstars,
                                    game, hideBooked) {
    let data = {};

    // Get roster division and member weights
    let matchDivisionsWeights = await getAutoBookerDivisionAndMembersWeights(roster, availableSuperstars,
        availableTagTeams, availableTrios, championships, style);

    // Get match contracts
    let parentEvent = await getParentEventForEvent(parentEvents, latestEvent);
    let matchContracts = parentEvents.length > 0 ? await getEventMatchContracts(parentEvent) : [];

    // Book matches based on numberToBook
    data['bookedMatches'] = [];
    for (const x of Array(numberToBook).keys()) {

        // Select a division, select top division in first match (main event)
        let selectedDivision;
        if (x === 0) {
            selectedDivision = [...matchDivisionsWeights].sort((a, b) => b[1] - a[1])[0][0]
        }
        else {
            selectedDivision = weightedChoice(matchDivisionsWeights)
        }

        // Book match if 2 or more participants are present in division
        if (selectedDivision.length >= 2) {
            let autoBookData = await autoBookMatch(selectedDivision, latestEvent, championships, company, gimmickMatches,
                pastEventMatches, game, singlesFeuds, tagFeuds, [...feudItems], companyLogs, style,
                rosterSuperstars, hideBooked, matchDivisionsWeights, matchContracts);
            data['bookedMatches'].push(autoBookData['bookedMatch']);

            // Update variables
            matchDivisionsWeights = autoBookData['divisionsWeights'];
            singlesFeuds = autoBookData['singlesFeuds'];
            tagFeuds = autoBookData['tagFeuds'];
            pastEventMatches = autoBookData['pastEventMatches'];
            matchContracts = autoBookData['matchContracts'];
        }
    }

    return data;
}

async function autoBookWeeklyEvent(company, roster, availableSuperstars, availableTagTeams, availableTrios,
                                   championships, style, gimmickMatches, latestEvent, parentEvents, numberToBook,
                                   singlesFeuds, tagFeuds, pastEventMatches, feudItems, companyLogs, rosterSuperstars,
                                   game, hideBooked) {
    let data = {};
    let itemsUsed = [];
    const segmentItems = await getCompanySegmentItems(company);
    const onlyProductionSegments = latestEvent.activeEffect === 'production_packages_only';
    const segmentRate = company.style === 'entertainment' ? 50 : 25;

    // Get roster division and member weights
    let matchDivisionsWeights = await getAutoBookerDivisionAndMembersWeights(roster, availableSuperstars,
        availableTagTeams, availableTrios, championships, style);
    let segmentDivisionsWeights = await getAutoBookerDivisionAndMembersWeights(roster, availableSuperstars,
        availableTagTeams, availableTrios, championships, style, true);

    // Book matches/segments based on numberToBook
    data['bookedMatches'] = [];
    data['bookedSegments'] = [];
    for (const x of Array(numberToBook).keys()) {
        let bookSegment = getRandomInt(0, 100) <= segmentRate || onlyProductionSegments;

        // Select a division, select top division in main event
        let selectedDivision;
        let selectedDivisionWeights = bookSegment ? segmentDivisionsWeights : matchDivisionsWeights;
        if (x === 0) {
            selectedDivision = [...selectedDivisionWeights].sort((a, b) => b[1] - a[1])[0][0]
        } else {
            selectedDivision = weightedChoice(selectedDivisionWeights)
        }

        // Book if 2 or more participants are present in division
        if (selectedDivision.length >= 2) {

            // Book segment
            if (bookSegment) {
                let autoBookData = await autoBookSegment(selectedDivision, latestEvent, championships, company, game,
                    singlesFeuds, tagFeuds, feudItems, segmentItems, itemsUsed, companyLogs, style, rosterSuperstars,
                    hideBooked, segmentDivisionsWeights);
                if (autoBookData) {
                    data['bookedSegments'].push(autoBookData['bookedSegment']);

                    // Update variables
                    segmentDivisionsWeights = autoBookData['divisionsWeights'];
                    singlesFeuds = autoBookData['singlesFeuds'];
                    tagFeuds = autoBookData['tagFeuds'];
                    itemsUsed = autoBookData['itemsUsed'];
                }
            }
            // Book match
            else {
                let autoBookData = await autoBookMatch(selectedDivision, latestEvent, championships, company,
                    gimmickMatches, pastEventMatches, game, singlesFeuds, tagFeuds, feudItems,
                    companyLogs, style, rosterSuperstars, hideBooked, matchDivisionsWeights);
                data['bookedMatches'].push(autoBookData['bookedMatch']);

                // Update variables
                matchDivisionsWeights = autoBookData['divisionsWeights'];
                singlesFeuds = autoBookData['singlesFeuds'];
                tagFeuds = autoBookData['tagFeuds'];
                pastEventMatches = autoBookData['pastEventMatches'];
            }
        }
    }

    return data;
}

async function getAutoBookerDivisionAndMembersWeights(roster, superstars, tagTeams, trios, championships, style,
                                                      segment = false) {  // get_roster_divisions_with_weights
    // Returns data in format [ [ [member, memberWeight], [member, memberWeight] ], divisionWeight ]
    let weights = [];

    // Get singles, tag team and trio division members
    let maleSinglesDivisionMembers = superstars.filter(x => x.gender === 'male' || x.gender === 'non-binary');
    let femaleSinglesDivisionMembers = superstars.filter(x => x.gender === 'female' || x.gender === 'non-binary');
    let maleTagTeamDivisionMembers = [];
    let femaleTagTeamDivisionMembers = [];
    for (const x of tagTeams) {
        let tagTeamSuperstars = await getTagTeamSuperstars(x);
        if (tagTeamSuperstars.every(x => x.gender === 'male' || x.gender === 'non-binary')) {
            maleTagTeamDivisionMembers.push(x)
        }
        if (tagTeamSuperstars.every(x => x.gender === 'female' || x.gender === 'non-binary')) {
            femaleTagTeamDivisionMembers.push(x)
        }
    }
    let maleTrioDivisionMembers = [];
    let femaleTrioDivisionMembers = [];
    for (const x of trios) {
        let trioSuperstars = await getTagTeamSuperstars(x);
        if (trioSuperstars.every(x => x.gender === 'male' || x.gender === 'non-binary')) {
            maleTrioDivisionMembers.push(x)
        }
        if (trioSuperstars.every(x => x.gender === 'female' || x.gender === 'non-binary')) {
            femaleTrioDivisionMembers.push(x)
        }
    }

    // Get division members' weights, add division weights based on number of division members
    let totalMembers = maleSinglesDivisionMembers.length + femaleSinglesDivisionMembers.length +
        maleTagTeamDivisionMembers.length +  femaleTagTeamDivisionMembers.length + maleTrioDivisionMembers.length +
        femaleTrioDivisionMembers.length;
    for (const x of [maleSinglesDivisionMembers, femaleSinglesDivisionMembers, maleTagTeamDivisionMembers,
        femaleTagTeamDivisionMembers, maleTrioDivisionMembers, femaleTrioDivisionMembers]) {
        let divisionMembersWeights = await getDivisionMembersWeights(x, championships, style, segment);
        let numberOfMembers = divisionMembersWeights.length;
        if ([maleTagTeamDivisionMembers, femaleTagTeamDivisionMembers].includes(x)) {
            numberOfMembers *= 2
        }
        else if ([maleTrioDivisionMembers, femaleTrioDivisionMembers].includes(x)) {
            numberOfMembers *= 3
        }
        weights.push([divisionMembersWeights, numberOfMembers / totalMembers]);
    }

    return weights;
}

async function getDivisionMembersWeights(divisionMembers, championships, style, segment) {
    let divisionMembersWeights = [];
    if (divisionMembers.length <= 0) {
        return divisionMembersWeights
    }

    // Filter championships
    if (divisionMembers[0].objectName === 'superstar') {
        championships = championships.filter(x => !x.tagTeam && !x.trio)
    }
    else if (divisionMembers[0].objectName === 'tagTeam') {
        let trio = divisionMembers[0].trio;
        if (trio) {
            championships = championships.filter(x => x.trio)
        }
        else {
            championships = championships.filter(x => x.tagTeam)
        }
    }

    // Get segment or match weights
    for (const x of divisionMembers) {
        // Skip temporary teams
        if (x.objectName === 'tagTeam ' && x.temporary) {
            continue;
        }
        let weight = await getDivisionMemberWeight(x, championships, style, segment);
        divisionMembersWeights.push([x, weight]);
    }

    return divisionMembersWeights;
}

async function getDivisionMemberWeight(x, championships, style, segment) {
    // Get superstars
    let superstars = await getRosterMemberSuperstars(x);

    // Set initial weight using popularity
    let weight;
    let popularity = await getRosterMemberPopularity(x);
    popularity = limitValueBetweenRange(popularity, 0, 10000);
    if (style === 'quality') {
        weight = Math.exp(popularity / 70) / 1000;
        if (getRankFromPopularity(popularity) === 'Main Eventer') {
            weight *= 5
        }
    }
    else if (style === 'bizarro') {
        weight = Math.exp(popularity / 250)
    }
    else {
        weight = Math.exp(popularity / 70) / 100
    }

    // Part timer
    if (superstars.some(y => y.partTimer)) {
        let partTimerDivisor = 1.3;
        if (style === 'long-term') {
            partTimerDivisor = 2.5;
            let age = Math.round(superstars.reduce((n, {age}) => n + age, 0) / superstars.length);
            weight *= getAutoBookerAgeMultiplier(age);  // Move outside of part timer check
        }
        else if (style === 'bizarro') {
            partTimerDivisor = 0.2
        }
        weight /= partTimerDivisor;
    }

    // Hot/stale
    if (superstars.some(x => x.hot)) {
        if (style === 'quality') {
            weight *= 20
        }
        else if (style === 'terrible') {
            weight *= 0.5
        }
        else {
            weight *= 10
        }
    }
    if (superstars.some(x => x.stale)) {
        if (style === 'quality') {
            weight *= 0.05
        }
        else if (style === 'terrible') {
            weight *= 10
        }
        else {
            weight *= 0.2
        }
    }

    if (segment) {
        // Charisma
        for (const y of superstars) {
            let charismaMultipliers;
            if (style === 'quality') {
                charismaMultipliers = {1: 0.1, 2: 0.5, 3: 30, 4: 30};
            }
            else if (style === 'terrible') {
                charismaMultipliers = {1: 20, 2: 1, 3: 0.1, 4: 0.1};
            }
            else {
                charismaMultipliers = {1: 1, 2: 3, 3: 5, 4: 5};
            }
            weight *= charismaMultipliers[getSuperstarCharisma(y)];
        }
    }
    else {
        // Champion
        let memberChampionships = championships.filter(y => y.superstarId === superstars[0].$loki);
        if (memberChampionships.length > 0) {
            if (style === 'quality') {
                weight *= 1000
            }
            else if (style === 'terrible') {
                weight *= 0.5
            }
            else {
                weight *= 5
            }
        }

        // Unhappy/leaving
        if (superstars.some(x => x.unhappy || x.contractMonthsRemaining !== null)) {
            weight *= 500;
        }

        // Workrate
        for (const y of superstars) {
            let workrateMultipliers;
            if (style === 'quality') {
                workrateMultipliers = {1: 0.1, 2: 0.5, 3: 30, 4: 30};
            }
            else if (style === 'terrible') {
                workrateMultipliers = {1: 20, 2: 1, 3: 0.1, 4: 0.1};
            }
            else {
                workrateMultipliers = {1: 1, 2: 3, 3: 5, 4: 5};
            }
            weight *= workrateMultipliers[getSuperstarWorkrate(y)];
        }
    }

    return Math.ceil(weight);
}

function getAutoBookerAgeMultiplier(age) {
    if (age <= 25) {
        return 3
    }
    else if (age <= 32) {
        return 2
    }
    else if (age <= 38) {
        return 1
    }
    else {
        return 0.5
    }
}

async function checkAutoBookParticipantAvailability(p, rosterSuperstars, event, companyChampionships, bookedSuperstars = []) {
    if (p.objectName === 'superstar') {
        if (!checkAvailability(p, false) || p.fatigue >= 50 || !objectInArray(p, rosterSuperstars) ||
            objectInArray(p, bookedSuperstars)) {
            return false;
        }
        else if (!checkSuperstarBrandAvailability(event, p, getSuperstarChampionshipsFromCompanyChampionships(p,
            companyChampionships))) {
            return false;
        }
        else if ((event.activeEffect === 'no_men_bookable' && p.gender === 'male') ||
            (event.activeEffect === 'no_women_bookable' && p.gender === 'female')) {
            return false;
        }
        return true;
    }
    else if (p.objectName === 'tagTeam') {
        let available = true;
        let superstars = await getTagTeamSuperstars(p);
        if (superstars.length <= 0) {
            available = false;
        }
        for (const s of superstars) {
            if (!checkAvailability(s, false) || s.fatigue >= 50 || !objectInArray(s, rosterSuperstars) ||
                objectInArray(s, bookedSuperstars)) {
                available = false;
            }
            else if (!checkSuperstarBrandAvailability(event, s, getSuperstarChampionshipsFromCompanyChampionships(s,
                companyChampionships))) {
                available = false;
            }
            else if ((event.activeEffect === 'no_men_bookable' && s.gender === 'male') ||
                (event.activeEffect === 'no_women_bookable' && s.gender === 'female')) {
                available = false;
            }
        }
        return available;
    }
}

async function checkAutoBookFeudAvailability(feud, rosterSuperstars, event, companyChampionships, bookedSuperstars) {
    let available = true;
    let participantType;

    // Don't add feuds with less than 2 participants and faction feuds
    let feudParticipants = await getFeudParticipants(feud);
    if (feudParticipants.length < 2 || feudParticipants.some(x => x.objectName === 'faction')) {
        available = false;
    }
    else {
        // Check availability of feud participants
        for (const x of feudParticipants) {
            participantType = x.objectName;
            if (!(await checkAutoBookParticipantAvailability(x, rosterSuperstars, event, companyChampionships, bookedSuperstars))) {
                available = false;
            }
        }
    }

    return [available, participantType];
}

function getEventMatchesAndSegmentsToAutoBook(event, parentEvents, rosterSuperstars, productionFacility, numberOption,
                                              style) {  // get_event_segments_to_auto_book / get_event_matches_to_auto_book
    let minimum = getEventMinimumMatchesAndSegments(event, parentEvents, rosterSuperstars);
    let maximum = getEventMaximumMatchesAndSegments(event, parentEvents, productionFacility);

    if (numberOption === 'min') {
        return minimum;
    }
    else if (numberOption === 'max') {
        return maximum;
    }

    // Add weights, higher for more segments
    let weights = [];
    if (style === 'quality') {
        let i = 1;
        for (let step = maximum; step > minimum - 1; step--) {
            weights.push([step, i]);
            i++;
        }
    }
    else {
        let i = 1;
        for (let step = minimum; step < maximum + 1; step++) {
            weights.push([step, i]);
            i++;
        }
    }

    return weightedChoice(weights);
}

async function autoBookMatch(divisionWeights, event, championships, company, gimmickMatches, pastEventMatches, game,
                             singlesFeuds, tagFeuds, feudItems, companyLogs, style, rosterSuperstars,
                             hideBooked, matchDivisionsWeights, matchContracts = []) {  // auto_book_match
    let bookedParticipants;
    let bookedPastEventMatch;
    let bookedMatchContract;
    let selectedGimmickMatch;
    let matchChampionship;
    let weeklyShow = event.weeklyShow || event.name === 'Weekly Show';
    let femaleNames = ['women', 'woman', 'diva', 'female', 'ladies', 'knockout'];
    let useFeudChance = ['long-term', 'quality'].includes(style) ? 90 : 70;
    let usePastMatchChance = 20;
    let divisionMember = divisionWeights[0][0];
    let feudsOfType;
    let vacatedChampionships;
    let matchType;
    let matchSuperstars;

    // Get match type specific objects
    if (divisionMember.objectName === 'superstar') {
        matchType = 'singles';
        feudsOfType = singlesFeuds;
    }
    else if (divisionMember.objectName === 'tagTeam') {
        matchType = 'tag';
        feudsOfType = tagFeuds;
    }

    // Get match contract if holder is in this division and available
    let matchContract;
    for (const x of matchContracts) {
        let t = await getMatchContractTalent(x);
        if (t && objectInArray(t, divisionWeights.map(y => y[0])) &&
            (await getRosterMemberSuperstars(t)).every(y => checkSuperstarBrandAvailability(event, y,
                getSuperstarChampionshipsFromCompanyChampionships(y, championships)))) {
            matchContract = x;
        }
    }

    // Select participants
    if (matchContract) {
        // Select match contract holder and champion
        let holder = await getMatchContractTalent(matchContract);
        let selectData = await selectAutoBookChampion(divisionWeights, championships, holder, matchType);
        bookedParticipants = [holder, selectData['participant']];
        matchChampionship = selectData['championship'];
        bookedMatchContract = matchContract;
    }
    else {
        // Select feud participants
        if (feudsOfType.length > 0 && getRandomInt(0, 100) <= useFeudChance) {
            let selectData = await selectAutoBookFeudParticipants(feudsOfType, championships, weeklyShow);
            bookedParticipants = selectData['participants'];
            matchChampionship = selectData['championship'];
        }
        else {
            // Select past event match
            let pastEventMatchesOfType = pastEventMatches.filter(y => y.type === matchType);
            if (style !== 'bizarro' && pastEventMatchesOfType.length > 0 &&
                getRandomInt(0, 100) <= usePastMatchChance) {
                let selectData = await selectAutoBookMatchParticipants(pastEventMatchesOfType, championships, style,
                    weeklyShow);
                bookedParticipants = selectData['participants'];
                matchChampionship = selectData['championship'];
                bookedPastEventMatch = selectData['match'];
            }
            // Select new match
            else {
                let selectData = await selectAutoBookRandomParticipants([...divisionWeights], championships,
                    style, weeklyShow, matchType);
                bookedParticipants = selectData['participants'];
                matchChampionship = selectData['championship'];
            }
        }
    }

    // Get superstars in match
    matchSuperstars = (await Promise.all(bookedParticipants.map(x => getRosterMemberSuperstars(x)))).flat();

    // Get vacated championship if matchChampionship not present, based on participants' gender
    if (!matchChampionship) {
        if (divisionMember.objectName === 'superstar') {
            vacatedChampionships = championships.filter(x => x.vacated && !x.tagTeam && !x.trio && !x.booked);
        } else if (divisionMember.objectName === 'tagTeam') {
            vacatedChampionships = championships.filter(x => x.vacated && !x.booked);
            if (bookedParticipants[0].trio) {
                vacatedChampionships = vacatedChampionships.filter(x => x.trio)
            } else {
                vacatedChampionships = vacatedChampionships.filter(x => x.tagTeam)
            }
        }
        if (!(matchSuperstars.some(x => x.gender === 'male'))) {
            vacatedChampionships = vacatedChampionships.filter(x => femaleNames
                .some(y => x.name.toLowerCase().includes(y)));
        } else if (!(matchSuperstars.some(x => x.gender === 'female'))) {
            vacatedChampionships = vacatedChampionships.filter(x => !(femaleNames
                .some(y => x.name.toLowerCase().includes(y))));
        }
        if (vacatedChampionships.length > 0) {
            matchChampionship = getRandomChoice(vacatedChampionships)
        }
    }

    // Select gimmick match
    if (matchChampionship && matchChampionship.gimmickRestriction &&
        matchChampionship.gimmickRestriction['type'] === 'gimmick_match') {
        // Select gimmick match if championship is restricted to gimmick matches
        let availableGimmickMatches = gimmickMatches.filter(x => x.available && x.cost <= company.points);
        if (availableGimmickMatches.length > 0) {
            if (matchChampionship.gimmickRestriction['value']) {
                selectedGimmickMatch = availableGimmickMatches
                    .find(x => x.name === matchChampionship.gimmickRestriction['value']);
            }
            else {
                selectedGimmickMatch = await selectAutoBookerGimmickMatch(company, gimmickMatches, matchSuperstars,
                    matchChampionship, matchType);
            }
        }
    }
    else {
        // Select random gimmick match
        let gimmickMatchChance = getAutoBookerGimmickMatchChance(company, style, weeklyShow);
        if (getRandomInt(0, 100) <= gimmickMatchChance) {
            selectedGimmickMatch = await selectAutoBookerGimmickMatch(company, gimmickMatches, matchSuperstars,
                matchChampionship, matchType);
        }
    }

    // Check gimmick restriction for matchChampionship
    if (matchChampionship && !checkChampionshipGimmickRestriction(matchChampionship, selectedGimmickMatch, event,
        bookedParticipants)) {
        matchChampionship = null;
    }

    // Check gender for championship
    if (matchChampionship) {
        if (matchSuperstars.some(x => x.gender === 'male') && femaleNames
            .some(y => matchChampionship.name.toLowerCase().includes(y))) {
            matchChampionship = null
        }
    }

    // Remove all except first participant for open challenge
    if (selectedGimmickMatch && selectedGimmickMatch.consequence === 'random_opponent') {
        bookedParticipants = [bookedParticipants[0]];
        matchSuperstars = await getRosterMemberSuperstars(bookedParticipants[0]);
        // Remove matchChampionship if doesn't belong to first participant
        if (matchChampionship && !(matchSuperstars.some(x => x.$loki === matchChampionship.superstarId))) {
            matchChampionship = null;
        }
    }

    // Create match
    let bookedMatch = Match(event.matchesCount, matchType);
    await Matches.insert(bookedMatch);
    await addEventMatches(event, [bookedMatch]);
    event.matchesCount += 1;

    // Add participants
    await addMatchParticipants(bookedMatch, bookedParticipants);

    // Add championship
    if (matchChampionship) {
        await addMatchChampionships(bookedMatch, [matchChampionship]);
        for (const c of championships.filter(x => checkAreChampionshipsEqual(x, matchChampionship))) {
            c.booked = true;
        }
    }

    // Add gimmick match and its data
    if (selectedGimmickMatch) {
        await addGimmickMatchMatches(selectedGimmickMatch, [bookedMatch]);

        // Add custom object or location
        if (selectedGimmickMatch.generateObject) {
            bookedMatch.objectToClaim = generateGimmickMatchObjectName(bookedParticipants.map(x => x.name));
        }
        if (selectedGimmickMatch.generateLocation) {
            bookedMatch.location = generateGimmickMatchLocationName(bookedParticipants.map(x => x.name));
        }

        // Add referee id
        if (selectedGimmickMatch.rules === 'special_referee') {
            let refereeWeights = await getRefereeWeights(rosterSuperstars, bookedParticipants);
            let refereeSuperstar = weightedChoice(refereeWeights);
            bookedMatch.refereeId = refereeSuperstar.$loki;
        }
    }

    // Get all tag teams present
    let tagTeamsPresent = [];
    for (const s of matchSuperstars) {
        tagTeamsPresent.push(...(await getSuperstarTagTeams(s)).filter(x => !x.temporary));
    }
    tagTeamsPresent = getUniqueObjects(tagTeamsPresent);

    // Find existing feuds and add to match
    let bookedFeuds = await getParticipantFeuds(matchSuperstars, tagTeamsPresent);
    await addMatchFeuds(bookedMatch, bookedFeuds);

    // Start direct feuds
    for (const feud of bookedFeuds) {
        if (!feud.started && feud.directFeudEvent && !feud.ended) {
            feud.startMonth = game.monthCount;
            feud.startYear = game.year;
            feud.runBlowoff = false;
        }
    }

    // Add managers
    if (globalUser.paid) {
        setManagersForMatchOrSegment(bookedMatch, matchSuperstars, rosterSuperstars, event)
    }

    // Set hidden if hideBooked
    if (hideBooked) {
        bookedMatch.hidden = true;
    }

    // Add hype to match
    bookedMatch.hype = await getHypeForMatch(bookedParticipants, bookedFeuds, [matchChampionship], company.style);

    // Chance to add blowoff to feuds
    for (const feud of bookedFeuds) {
        await checkToAddAutoBookFeudBlowoff(feud, bookedParticipants, matchType)
    }

    // Chance to create new feud if match has 2 participants and no feuds
    if (bookedFeuds.length <= 0 && bookedParticipants.length === 2) {
        await checkToCreateAutoBookFeud(bookedParticipants, bookedMatch, feudItems, company, style, matchType,
            companyLogs, game.monthCount, game.year)
    }

    // Process and get data to return
    let data = await processAutoBookMatchData(matchSuperstars, tagTeamsPresent, bookedMatch, bookedFeuds,
        bookedPastEventMatch, bookedMatchContract, singlesFeuds, tagFeuds, pastEventMatches, matchContracts,
        matchDivisionsWeights);
    data['bookedMatch'] = bookedMatch;

    return data;
}

async function selectAutoBookChampion(weights, championships, holder, matchType) {  // select_champion_from_participants
    let data = {};
    let championship;
    let championsWeights = [];
    let trio = matchType === 'tag' ? weights[0][0].trio : false;
    let prestigeWeights = {'S': 4, 'A': 3, 'B': 2, 'C': 1};
    let participantsChampionshipsData = {};

    // Don't select match contract holder
    let newWeights = weights.filter(x => x[0].$loki !== holder.$loki);

    // Add to championsWeights if champion
    for (const x of newWeights) {
        let participant = x[0];
        let participantChampionships;
        if (matchType === 'singles') {
            participantChampionships = championships.filter(y => y.superstarId === participant.$loki && !y.matchContract &&
                !y.tagTeam && !y.trio);
        }
        else if (matchType === 'tag') {
            let participantSuperstars = await getTagTeamSuperstars(participant);
            participantChampionships = championships.filter(y => y.superstarId === participantSuperstars[0].$loki &&
                !y.matchContract);
            if (trio) {
                participantChampionships = participantChampionships.filter(y => y.trio)
            }
            else {
                participantChampionships = participantChampionships.filter(y => y.tagTeam)
            }
        }

        if (participantChampionships.length > 0) {
            let weight = x[1];
            // Increase weight by championship's prestige
            for (const c of participantChampionships) {
                weight *= (prestigeWeights[c.prestige] / 3) ** 2
            }
            championsWeights.push([participant, weight]);
            participantsChampionshipsData[participant.$loki] = participantChampionships;
        }
    }

    // Add random participant if no champions found
    if (championsWeights.length <= 0) {
        championsWeights.push([getRandomChoice(newWeights)[0], 1])
    }

    // Get participant
    let participant = weightedChoice(championsWeights);

    // Get championship
    if (participantsChampionshipsData[participant.$loki]) {
        championship = getRandomChoice(participantsChampionshipsData[participant.$loki]);
    }

    // Get data to return
    data['participant'] = participant;
    data['championship'] = championship;

    return data;
}

async function selectAutoBookFeudParticipants(feuds, championships, weeklyShow) {  // select_participants_from_feuds
    let data = {};
    let feudWeights = [];

    // Get feud weights
    for (const feud of feuds) {
        let weight = 0;
        if (feud.hype > 0) {
            weight += (feud.hype / 4) ** 2
        }
        if (feud.hot) {
            weight *= 2
        }
        feudWeights.push([feud, weight]);
    }

    // Get feud
    let feud = weightedChoice(feudWeights);

    // Get championship from participants
    let participants = await getFeudParticipants(feud);
    if (championships) {
        data['championship'] = await getAutoBookerChampionshipFromParticipants(participants, championships, weeklyShow);
    }

    // Get data to return
    data['participants'] = participants;

    return data;
}

async function selectAutoBookMatchParticipants(matches, championships, style, weeklyShow) {  // select_match_from_previous_event
    let data = {};
    let matchWeights = [];

    // Get match weights
    for (const match of matches) {
        let order = match.order + 1;
        if (order === 0) {
            order = 1
        }
        let weight = 10 / (order + 1);
        if (style === 'quality' && match.rating >= 4) {
            weight += match.rating ** 2
        }
        matchWeights.push([match, weight]);
    }

    // Get match
    let match = weightedChoice(matchWeights);

    // Get championship from participants
    let participants = await getMatchParticipants(match);
    let championship = await getAutoBookerChampionshipFromParticipants(participants, championships, weeklyShow);

    // Get data to return
    data['participants'] = participants;
    data['championship'] = championship;
    data['match'] = match;

    return data;
}

async function selectAutoBookRandomParticipants(weights, championships, style, weeklyShow, matchType) {
    let data = {};
    let participants = [];

    // Get number of participants
    let numberOfParticipantsWeights;
    if (matchType === 'singles') {
        numberOfParticipantsWeights = [[2, 78], [3, 7], [4, 5], [5, 2], [6, 3], [7, 1], [8, 2], [9, 1], [10, 1]];
    }
    else {
        numberOfParticipantsWeights = [[2, 60], [3, 13], [4, 10], [5, 5], [6, 5], [7, 2], [8, 3], [9, 1], [10, 1]]
    }
    let numberOfParticipants = weightedChoice(numberOfParticipantsWeights);
    if (numberOfParticipants > weights.length) {
        numberOfParticipants = 2
    }

    // Get participants
    for (const x of Array(numberOfParticipants).keys()) {
        let participant = weightedChoice(weights);
        weights = weights.filter(y => y[0].$loki !== participant.$loki);
        participants.push(participant);

        // If long term and main eventer selected, increase weight for lower popularity superstars
        let popularity = await getRosterMemberPopularity(x);
        if (x === 0 && style === 'long-term' && getRankFromPopularity(popularity) === 'Main Eventer') {
            for (const [index, element] of Object.entries(weights)) {
                let newWeight = element[1];
                let p = await getRosterMemberPopularity(element[0]);
                if (getRankFromPopularity(p) !== 'Main Eventer') {
                    newWeight *= 5
                }
                weights[index][1] = newWeight;
            }
        }

        // Add alignment bias
        if (x === 0 && style !== 'bizarro' && numberOfParticipants === 2) {
            let alignmentMultiplier = style === 'quality' ? 40 : 10;
            let participantAlignment = await getRosterMemberAlignment(participant);
            if (participantAlignment === 'face' || participantAlignment === 'heel') {
                for (const [index, element] of Object.entries(weights)) {
                    let newWeight = element[1];
                    let elementAlignment = await getRosterMemberAlignment(element[0]);
                    if (participantAlignment === 'face') {
                        if (elementAlignment === 'heel') {
                            newWeight *= alignmentMultiplier
                        }
                        else if (!elementAlignment) {
                            newWeight /= 2
                        }
                    }
                    else if (participantAlignment === 'heel') {
                        if (elementAlignment === 'face') {
                            newWeight *= alignmentMultiplier
                        }
                        else if (elementAlignment === 'heel') {
                            newWeight /= 5
                        }
                    }
                    weights[index][1] = newWeight;
                }
            }
        }

        // Add brand name bias
        let participantBrandName = await getRosterMemberBrandName(participant);
        if (x === 0 && style !== 'bizarro' && participantBrandName) {
            for (const [index, element] of Object.entries(weights)) {
                let newWeight = element[1];
                let elementBrandName = await getRosterMemberBrandName(element[0]);
                if (elementBrandName === participantBrandName || !elementBrandName) {
                    newWeight *= 20
                }
                weights[index][1] = newWeight;
            }
        }
    }

    // Get championship from participants
    let championship = await getAutoBookerChampionshipFromParticipants(participants, championships, weeklyShow);

    // Get data to return
    data['participants'] = participants;
    data['championship'] = championship;

    return data;
}

async function getAutoBookerChampionshipFromParticipants(participants, championships, weeklyShow) {
    let championship;

    // Get participants' championships
    let participantsChampionships = [];
    for (const x of participants) {
        let participantChampionships = [];
        if (x.objectName === 'superstar') {
            participantChampionships = championships.filter(y => y.superstarId === x.$loki && !y.tagTeam && !y.trio);
        }
        else if (x.objectName === 'tagTeam') {
            let participantSuperstars = await getTagTeamSuperstars(x);
            participantChampionships = championships.filter(y => y.superstarId === participantSuperstars[0].$loki &&
                !y.matchContract);
            if (participants.every(y => y.trio)) {
                participantChampionships = participantChampionships.filter(y => y.trio)
            }
            else {
                participantChampionships = participantChampionships.filter(y => y.tagTeam)
            }
        }
        participantsChampionships.push(...participantChampionships);
    }

    // Chance to select a participant championship
    let addChampionshipChance = weeklyShow ? 20 : 95;
    if (participantsChampionships.length > 0 && getRandomInt(0, 100) <= addChampionshipChance) {
        championship = getRandomChoice(participantsChampionships)
    }

    return championship;
}

function getAutoBookerGimmickMatchChance(company, autoBookerStyle, weeklyShow) {  // get_match_gimmick_chance
    let percentage;
    if (company.style === 'entertainment') {
        percentage = 12
    }
    else {
        percentage = 2
    }

    if (autoBookerStyle === 'bizarro') {
        percentage *= 3
    }
    else if (autoBookerStyle === 'quality') {
        percentage *= 2
    }
    else if (autoBookerStyle === 'terrible') {
        percentage *= 0.5
    }

    if (weeklyShow) {
        percentage /= 2
    }
    return percentage;
}

async function selectAutoBookerGimmickMatch(company, gimmickMatches, matchSuperstars, championship, style) {  // select_match_gimmick
    // Remove unavailable, not enough points, battle royal and cash in matches
    let filteredGimmickMatches = gimmickMatches.filter(x => x.available && x.rules !== 'battle_royal' &&
        !x.cashinContractName && x.cost <= company.points);

    // Chance to select match contract matches
    let availableGimmickMatches;
    if (getRandomInt(0, 100) <= 5 && !championship) {
        availableGimmickMatches = filteredGimmickMatches.filter(x => x.matchContractName)
    } else {
        availableGimmickMatches = filteredGimmickMatches.filter(x => !x.matchContractName)
    }

    // Check/chance to remove femme fatale match
    if (matchSuperstars.some(x => x.gender !== 'female') || getRandomInt(0, 100) <= 75) {
        availableGimmickMatches = availableGimmickMatches.filter(x => !(x.name === 'Femme Fatale' && x.special))
    }

    // Chance to remove pole match
    if (getRandomInt(0, 100) <= 75) {
        availableGimmickMatches = availableGimmickMatches.filter(x => !x.generateObject)
    }

    // Chance to remove some consequence matches
    if (getRandomInt(0, 100) <= 90 && style !== 'bizarro') {
        availableGimmickMatches = availableGimmickMatches.filter(x => !(['remove_hair', 'add_hair',
            'remove_mask', 'remove_mask_or_hair'].includes(x.consequence)))
    }

    // Select gimmick match from available
    if (availableGimmickMatches.length > 0) {
        let selectedGimmickMatch = getRandomChoice(availableGimmickMatches);
        company.points -= selectedGimmickMatch.cost;
        return selectedGimmickMatch;
    }

    return null;
}

async function checkToAddAutoBookFeudBlowoff(feud, bookedParticipants, matchType) {
    if (feud.stalenessCounter > 0) {
        // Get blowoff chance based on staleness
        let feudParticipants = await getFeudParticipants(feud);
        let blowoffChance = await getAutoBookFeudBlowoffChance(feud, feudParticipants);
        if (getRandomInt(0, 100) <= blowoffChance) {
            let feudItem = await getFeudFeudItem(feud);
            let runBlowoff = true;
            // Only blowoff consequence feud in same match type as participant type
            if (feudItem.consequence) {
                if (feudParticipants[0].objectName === 'superstar' && matchType !== 'singles') {
                    runBlowoff = false;
                }
                else if (feudParticipants[0].objectName === 'faction' && matchType !== 'tag') {
                    runBlowoff = false;
                }
                else if (feudParticipants[0].objectName === 'tagTeam') {
                    if (matchType !== 'tag') {
                        runBlowoff = false;
                    }
                    if (feudParticipants.every(x => x.trio)) {
                        // Allow trios feud blowoff in tag or trios match
                        for (const x of bookedParticipants) {
                            let participantSuperstars = await getTagTeamSuperstars(x);
                            if (!([2, 3].includes(participantSuperstars.length))) {
                                runBlowoff = false;
                            }
                        }
                    }
                    else {
                        // Allow tag feud blowoff in tag match only
                        for (const x of bookedParticipants) {
                            let participantSuperstars = await getTagTeamSuperstars(x);
                            if (participantSuperstars.length !== 2) {
                                runBlowoff = false;
                            }
                        }
                    }
                }
            }
            if (runBlowoff) {
                feud.runBlowoff = true;
            }
        }
    }
}

async function getAutoBookFeudBlowoffChance(feud, feudParticipants) {
    let staleLength = await getMatchesForStaleFeud(feud, feudParticipants);
    let blowoffChance = -1;
    if (feud.stalenessCounter >= staleLength) {
        blowoffChance = 90
    }
    else if (feud.stalenessCounter >= (staleLength * 0.75)) {
        blowoffChance = 10
    }
    return blowoffChance;
}

async function checkToCreateAutoBookFeud(bookedParticipants, bookedMatchOrSegment, feudItems, company, style,
                                         participantType, companyLogs, month, year) {
    // Get create feud chance
    let createFeudChance = 50;
    if (style === 'long-term') {
        createFeudChance = 75
    }
    else if (style === 'quality') {
        createFeudChance = 90
    }
    // Lower chance if any participant has existing feuds
    let participantsFeuds = (await Promise.all(bookedParticipants.map(x => getRosterMemberFeuds(x)))).flat()
        .filter(x => x.started && !x.ended);
    if (participantsFeuds.length > 0) {
        createFeudChance /= 2
    }

    // Create feud
    if (getRandomInt(0, 100) <= createFeudChance) {

        // Get available feud items
        const feudItemsData = await getAvailableFeudItems(feudItems, bookedParticipants, company.storyName,
            month, year, companyLogs, participantType);
        let availableFeudItems = feudItemsData[0];
        if (availableFeudItems.length > 0) {

            // Check minimum previous feud boost
            let previousFeudBoost = await getFeudCreationPreviousFeudsBoost({}, bookedParticipants, month, year);
            let minimumPreviousFeudBoost = getAutoBookerMinimumPreviousFeudBoost(style);
            if (previousFeudBoost >= minimumPreviousFeudBoost) {

                // Get feud name
                let feudItem = getRandomChoice(availableFeudItems);
                let feudName = feudItem.name;
                let generateFeudNameChance = style !== 'bizarro' ? 30 : 80;
                if (feudItem.name === 'Grudge' && getRandomInt(0, 100) <= generateFeudNameChance) {
                    feudName = generateRandomFeudItemName()
                }

                // Add new feud attributes
                let newFeud = Feud();
                newFeud.participants = bookedParticipants.map(x => x.name);
                newFeud.name = feudName;
                newFeud.hype = feudItem.baseHype;
                newFeud.startMonth = month;
                newFeud.startYear = year;

                // Get who to apply consequence to
                if (feudItem.oneWayConsequence) {
                    newFeud.consequenceAppliedTo = await getFeudConsequenceAppliedTo(feudItem, bookedParticipants,
                        participantType);
                }

                // Save feud to db
                await Feuds.insert(newFeud);

                // Add feud to feud item and roster
                await addFeudItemFeuds(feudItem, [newFeud]);
                let mainRoster = await getCompanyRosterByType(company, 'main');
                await addRosterFeuds(mainRoster, [newFeud]);

                // Add feud to match/segment
                if (bookedMatchOrSegment.objectName === 'match') {
                    await addMatchFeuds(bookedMatchOrSegment, [newFeud]);
                }
                else if (bookedMatchOrSegment.objectName === 'segment') {
                    await addSegmentFeuds(bookedMatchOrSegment, [newFeud]);
                }

                // Add feud to participants
                if (participantType === 'singles') {
                    await addFeudSuperstars(newFeud, bookedParticipants)
                }
                else if (participantType === 'tag') {
                    await addFeudTagTeams(newFeud, bookedParticipants)
                }
            }
        }
    }
}

function getAutoBookerMinimumPreviousFeudBoost(style) {  // get_auto_booker_minimum_feud_starting_hype
    let minimumPreviousFeudBoost = -15;
    if (style === 'quality') {
        minimumPreviousFeudBoost = 0
    }
    else if (style === 'terrible') {
        minimumPreviousFeudBoost = -90
    }
    return minimumPreviousFeudBoost;
}

async function selectAutoBookSegmentParticipants(weights, numberOfParticipants, style) {  // select_segment_participants
    let data = {};
    let participants = [];

    // Minimum participants
    if (numberOfParticipants > weights.length) {
        numberOfParticipants = 1
    }

    // Get participants
    for (const x of Array(numberOfParticipants).keys()) {
        let participant = weightedChoice(weights);
        weights = weights.filter(y => y[0].$loki !== participant.$loki);
        participants.push(participant);

        // If long term and main eventer selected, increase weight for lower popularity superstars
        let popularity = await getRosterMemberPopularity(x);
        if (x === 0 && style === 'long-term' && getRankFromPopularity(popularity) === 'Main Eventer') {
            for (const [index, element] of Object.entries(weights)) {
                let newWeight = element[1];
                let p = await getRosterMemberPopularity(element[0]);
                if (getRankFromPopularity(p) !== 'Main Eventer') {
                    newWeight *= 5
                }
                weights[index][1] = newWeight;
            }
        }
    }

    // Get data to return
    data['participants'] = participants;

    return data;
}

async function autoBookSegment(divisionWeights, event, championships, company, game, singlesFeuds, tagFeuds, feudItems,
                               segmentItems, itemsUsed, companyLogs, style, rosterSuperstars, hideBooked,
                               segmentDivisionsWeights) {  // auto_book_segment
    const staffMembers = await getCompanyStaff(company);
    const availableStaffMembers = getAvailableStaffMembers(staffMembers);
    const currentGame = await getCurrentGame();
    let useFeudChance = ['long-term', 'quality'].includes(style) ? 80 : 60;
    let divisionMember = divisionWeights[0][0];
    let feudsOfType;
    let participantType;
    let allSuperstars;

    // Filter segment items
    segmentItems = filterAutoBookerSegmentItems(segmentItems, company);

    // Get participant type specific objects
    if (divisionMember.objectName === 'superstar') {
        participantType = 'singles';
        feudsOfType = singlesFeuds;
    }
    else if (divisionMember.objectName === 'tagTeam') {
        participantType = 'tag';
        feudsOfType = tagFeuds;
    }

    // Get number of participants depending on minParticipants of segment items
    const items = segmentItems.map(x => x['minParticipants']);
    const numberOfParticipantsWeights = [...new Set(items)].map(x => {
        return [x, items.filter(y => y === x).length]
    });
    const numberOfParticipants = weightedChoice(numberOfParticipantsWeights);

    // Select segment participants
    let selectData;
    if (feudsOfType.length > 0 && getRandomInt(0, 100) <= useFeudChance) {
        selectData = await selectAutoBookFeudParticipants(feudsOfType);
    }
    else {
        selectData = await selectAutoBookSegmentParticipants([...divisionWeights], numberOfParticipants,
            style);
    }
    let bookedParticipants = selectData['participants'];

    // Create segment, add to event
    let bookedSegment = Segment();
    bookedSegment.order = event.matchesCount;
    await Segments.insert(bookedSegment);
    await addEventSegments(event, [bookedSegment]);
    event.matchesCount += 1;

    // Add participants
    await addSegmentParticipants(bookedSegment, bookedParticipants);

    // Get all superstars and tag teams in segment
    allSuperstars = (await Promise.all(bookedParticipants.map(x => getRosterMemberSuperstars(x)))).flat();
    let tagTeamsPresent = [];
    for (const s of allSuperstars) {
        tagTeamsPresent.push(...(await getSuperstarTagTeams(s)).filter(x => !x.temporary));
    }
    tagTeamsPresent = getUniqueObjects(tagTeamsPresent);

    // Find existing feuds and add to segment
    let bookedFeuds = await getParticipantFeuds(allSuperstars, tagTeamsPresent);
    await addSegmentFeuds(bookedSegment, bookedFeuds);

    // Get available segment items
    let availableSegmentItems = await getAutoBookAvailableSegmentItems(segmentItems, bookedParticipants, companyLogs,
        game.monthCount, game.year, championships, availableStaffMembers, event, bookedFeuds, participantType);

    // Get segment item
    let bookedSegmentItem;
    if (availableSegmentItems.length > 0) {
        let availableSegmentItemsWeights = getAutoBookAvailableSegmentItemsWeights(availableSegmentItems, style, itemsUsed);
        bookedSegmentItem = weightedChoice(availableSegmentItemsWeights);
    }
    else {
        // Fallback segment item if none available
        bookedSegmentItem = segmentItems.find(x => x.name === 'Promo Battle');
    }
    await addSegmentItemSegments(bookedSegmentItem, [bookedSegment]);

    // Add one way consequence
    if (bookedSegmentItem.oneWayConsequence) {
        bookedSegment.consequenceAppliedTo = await getSegmentConsequenceAppliedTo(bookedSegmentItem, bookedParticipants);
    }

    // Set segment item to used
    if (bookedSegmentItem.special && !currentGame.settings['no_special_items_cooldown']) {
        bookedSegmentItem.used = true;
    }

    // Add managers
    if (!bookedSegmentItem.special && globalUser.paid) {
        setManagersForMatchOrSegment(bookedSegment, allSuperstars, rosterSuperstars, event)
    }

    // Set hidden if hideBooked
    if (hideBooked) {
        bookedSegment.hidden = true;
    }

    // Add hype
    bookedSegment.hype = await getHypeForSegment(bookedParticipants, bookedFeuds, bookedSegmentItem);

    // Chance to create new feud if segment has 2 participants and no feuds
    if (bookedFeuds.length <= 0 && bookedParticipants.length === 2) {
        await checkToCreateAutoBookFeud(bookedParticipants, bookedSegment, feudItems, company, style, participantType,
            companyLogs, game.monthCount, game.year)
    }

    // Process and get data to return
    let data = processAutoBookSegmentData(allSuperstars, tagTeamsPresent, bookedSegment, bookedFeuds,
        bookedSegmentItem, singlesFeuds, tagFeuds, itemsUsed, segmentDivisionsWeights);
    data['bookedSegment'] = bookedSegment;

    return data;
}

function filterAutoBookerSegmentItems(segmentItems, company) {
    // Remove producer/buyout segment items for competitors since they don't have staff
    if (!company.userControlled) {
        segmentItems = segmentItems.filter(x => !x.requirement ||
            (!(x.requirement.includes('producer')) && !(x.requirement.includes('buyout')))
        );
    }

    // Remove locked and used special segment items
    segmentItems = segmentItems.filter(x => x.unlocked && !x.used);

    // Remove faction/manager/brand/some other segment items for now
    return segmentItems.filter(x => !x.factionSegment && !(['boost-managed-talent', 'add-manager',
        'remove-manager', 'remove-managed-talent', 'draft-talent-to-brand', 'fire-superstar',
        'retire'].includes(x.consequence)) && !(['other-brand-talent', 'feud-present'].includes(x.requirement)));
}

async function getAutoBookAvailableSegmentItems(segmentItems, bookedParticipants, companyLogs, month, year, championships,
                                                availableStaffMembers, event, bookedFeuds, participantType) {
    let availableSegmentItems = [];

    // Check requirements and min/max participants
    for (const x of segmentItems) {
        if (bookedParticipants.length >= x.minParticipants && bookedParticipants.length <= x.maxParticipants &&
            (!x.feudOnly || bookedFeuds.length > 0)) {
            let requirementCheck = await checkSegmentItemRequirement(x, bookedParticipants, companyLogs, month, year,
                championships, availableStaffMembers);
            if (!requirementCheck['error']) {
                availableSegmentItems.push(x)
            }
        }
    }

    // Check for production packages only, don't book segment if none found
    if (event.activeEffect === 'production_packages_only') {
        availableSegmentItems = availableSegmentItems.filter(x => x.productionSegment)
    }

    // Don't add special segments for tag teams
    if (participantType !== 'singles') {
        availableSegmentItems = availableSegmentItems.filter(x => !x.special);
    }

    return availableSegmentItems;
}

function getAutoBookAvailableSegmentItemsWeights(availableSegmentItems, style, itemsUsed) {
    // Add weights for items used and silly items
    let availableSegmentItemsWeights;
    if (style === 'terrible') {
        availableSegmentItemsWeights = availableSegmentItems.map(x => objectInArray(x, itemsUsed) && x.oneAtATime ?
            [x, 5] : [x, 1]);
    }
    else {
        availableSegmentItemsWeights = availableSegmentItems.map(x => objectInArray(x, itemsUsed) && x.oneAtATime ?
            [x, 1] : [x, 5]);
    }
    if (style === 'quality') {
        availableSegmentItemsWeights = availableSegmentItemsWeights.map(x => x[0].silly ?
            [x[0], x[1] / 10] : [x[0], x[1]]);
    }
    else if (style === 'bizarro') {
        availableSegmentItemsWeights = availableSegmentItemsWeights.map(x => x[0].silly ?
            [x[0], x[1]] : [x[0], x[1] / 10]);
    }
    else {
        availableSegmentItemsWeights = availableSegmentItemsWeights.map(x => x[0].silly ?
            [x[0], x[1] / 2] : [x[0], x[1]]);
    }
    return availableSegmentItemsWeights;
}

async function processAutoBookMatchData(superstarsPresent, tagTeamsPresent, bookedMatch, bookedFeuds, bookedPastEventMatch,
                                        bookedMatchContract, singlesFeuds, tagFeuds, pastEventMatches, matchContracts,
                                        divisionsWeights) {
    /*
    Removes booked objects from arrays
     */

    let data = {};
    let participants = superstarsPresent.concat(tagTeamsPresent);

    // Remove booked participants from each division
    for (const [index, element] of Object.entries(divisionsWeights)) {
        divisionsWeights[index][0] = element[0].filter(y => !objectInArray(y[0], participants));
    }

    // Remove booked feuds
    for (const y of bookedFeuds) {
        singlesFeuds = singlesFeuds.filter(f => f.$loki !== y.$loki);
        tagFeuds = tagFeuds.filter(f => f.$loki !== y.$loki);
    }

    // Remove booked past event match
    if (bookedPastEventMatch) {
        pastEventMatches = pastEventMatches.filter(y => y.$loki !== bookedPastEventMatch.$loki)
    }

    // Remove booked match contract
    if (bookedMatchContract) {
        matchContracts = matchContracts.filter(x => x.$loki !== bookedMatchContract.$loki)
    }

    // Remove past event matches if any booked participant is present in them
    let matchesToRemove = [];
    for (const y of pastEventMatches) {
        let matchParticipants = await getMatchParticipants(y);
        if (participants.some(p => objectInArray(p, matchParticipants))) {
            matchesToRemove.push(y)
        }
    }
    pastEventMatches = pastEventMatches.filter(y => !objectInArray(y, matchesToRemove));

    // Remove feuds if any booked participant is present in them
    let feudsToRemove = [];
    for (const y of singlesFeuds.concat(tagFeuds)) {
        let feudParticipants = await getFeudParticipants(y);
        if (feudParticipants[0].objectName === participants[0].objectName &&
            participants.some(p => objectInArray(p, feudParticipants))) {
            feudsToRemove.push(y)
        }
    }
    singlesFeuds = singlesFeuds.filter(y => !objectInArray(y, feudsToRemove));
    tagFeuds = tagFeuds.filter(y => !objectInArray(y, feudsToRemove));

    // Get data to return
    data['divisionsWeights'] = divisionsWeights;
    data['singlesFeuds'] = singlesFeuds;
    data['tagFeuds'] = tagFeuds;
    data['pastEventMatches'] = pastEventMatches;
    data['matchContracts'] = matchContracts;

    return data;
}

function processAutoBookSegmentData(superstarsPresent, tagTeamsPresent, bookedSegment, bookedFeuds,
                                          bookedSegmentItem, singlesFeuds, tagFeuds, itemsUsed, divisionsWeights) {
    /*
    Removes booked objects from arrays
     */

    let data = {};
    let participants = superstarsPresent.concat(tagTeamsPresent);

    // Remove booked participants from each division
    for (const [index, element] of Object.entries(divisionsWeights)) {
        divisionsWeights[index][0] = element[0].filter(y => !objectInArray(y[0], participants));
    }

    // Remove booked feuds
    for (const y of bookedFeuds) {
        singlesFeuds = singlesFeuds.filter(f => f.$loki !== y.$loki);
        tagFeuds = tagFeuds.filter(f => f.$loki !== y.$loki);
    }

    // Add booked segment item
    if (bookedSegmentItem) {
        itemsUsed.push(bookedSegmentItem)
    }

    // Get data to return
    data['divisionsWeights'] = divisionsWeights;
    data['singlesFeuds'] = singlesFeuds;
    data['tagFeuds'] = tagFeuds;
    data['itemsUsed'] = itemsUsed;

    return data;
}

async function runSimulator({style, eventsToSimulate, simulatorOption} = {}) {  // simulate_month
    let data = {};
    const game = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(game);
    const mainCompany = await getGameMainUserCompany(game);
    const indieRoster = await getCompanyRosterByType(mainCompany, 'indie');
    const indieAvailableRoster = await getCompanyRosterByType(mainCompany, 'indieAvailable');
    const latestEvent = await getCompanyLatestEvent(userCompany)
    const parentEvents = await getCompanyParentEvents(userCompany);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const staffMembers = await getCompanyStaff(userCompany);
    const producers = getAvailableStaffMembersByRole(staffMembers, 'producer');
    const companyChampionships = await getCompanyChampionships(userCompany);
    const user = loadUser();

    // Check paid
    if (!globalUser.paid) {
        data['error'] = 'paid-only';
        return data;
    }

    // Check not local multiplayer
    if (game.settings['control_multiple_companies']) {
        data['error'] = 'local-multiplayer-disabled';
        return data;
    }

    // Check points
    let costData = {'standard': 10, 'long-term': 20, 'quality': 15, 'bizarro': 5, 'terrible': 2};
    let cost = costData[style] || 10;
    if (userCompany.points < cost) {
        data['error'] = 'points';
        return data;
    }

    try {
        // Auto book event
        await autoBookEvent({
            company: userCompany, roster: mainRoster, rosterSuperstars, latestEvent, producers, game,
            style, championships: companyChampionships, parentEvents
        });

        // Simulate event
        data['results'] = await simulateUserEvent(userCompany, latestEvent, user, indieRoster, indieAvailableRoster,
            mainRoster, staffMembers, game, mainCompany, companyChampionships, style, rosterSuperstars, parentEvents,
            simulatorOption, eventsToSimulate);

        // Simulate competitors
        let simulateCompetitorsData = await simulateCompetitors({simulating: true});

        // Simulate post event
        let updates = {};
        if (!latestEvent.weeklyShow) {
            updates = await simulatePostEvent(userCompany, user, style, simulatorOption);
        }

        // Process yearly rankings at start of year
        let currentMonth = await getCurrentMonth(userCompany, parentEvents);
        let nextEvent = await getCompanyLatestEvent(userCompany);
        if (currentMonth === 0 && !nextEvent.weeklyShow) {
            await processYearlyRanking(game, mainCompany);
        }

        userCompany.points -= cost;

        // Set news month/year
        game.newsMonth = game.monthCount;
        game.newsYear = game.year;

        db.saveDatabase();

        // Save to local storage
        await saveGameDataToLocalStorage(game);

        // Get data to return
        if (latestEvent.weeklyShow) {
            let nextMonthlyEvent = await getUpcomingMonthlyEventForWeeklyEvent(latestEvent, parentEvents, userCompany);
            data['weeklyShowName'] = getWeeklyEventName(latestEvent, nextMonthlyEvent, game.numberOfWeeklyShows);
        }
        data['event'] = latestEvent.name;
        data['year'] = userCompany.realYear;
        data['points'] = userCompany.points;
        data['fans'] = userCompany.fans;
        data['updates'] = updates;
        data['updates']['news'] = simulateCompetitorsData['news'];
    }
    catch (error) {
        captureError(error)
        data['error'] = 'simulator';
    }

    return data;
}

async function simulateUserEvent(company, event, user, indieRoster, indieAvailableRoster, mainRoster, staffMembers, game,
                                 mainCompany, companyChampionships, style, rosterSuperstars, parentEvents,
                                 userSimulatorOption, eventsToSimulate) {  // simulate_event
    let data = {};
    let eventMatches = await getEventMatches(event);
    let eventSegments = await getEventSegments(event);

    // Play event (adds event hype, poster etc)
    await playEvent({simulating: true, eventMatches, eventSegments});

    // Play matches and segments
    let matchOrSegmentResults = [];
    let matchesAndSegments = eventMatches.concat(eventSegments);
    matchesAndSegments.sort((a, b) => a.order - b.order);
    for (let matchOrSegment of matchesAndSegments) {
        let matchOrSegmentResult;
        if (matchOrSegment.objectName === 'segment') {
            matchOrSegmentResult = await processPlaySegment(matchOrSegment, null, eventSegments, event, company,
                mainCompany, game, mainRoster, indieRoster, indieAvailableRoster, null, rosterSuperstars,
                companyChampionships, staffMembers, [...matchesAndSegments]);
        } else {
            let winner = await simulateMatchWinner(matchOrSegment, style);
            let matchFinish = await getMatchFinishForSimulatorMatch(matchOrSegment, event, company);
            matchOrSegmentResult = await processPlayMatch(matchOrSegment, winner.name, event, company, mainCompany,
                mainRoster, indieRoster, game, {}, matchFinish, null, rosterSuperstars, parentEvents,
                companyChampionships, staffMembers, [...matchesAndSegments], null, true);
        }
        matchOrSegmentResults.push(matchOrSegmentResult);
    }

    // End event
    let eventResults = await endEvent(company, user, true, rosterSuperstars, userSimulatorOption, eventsToSimulate);

    // Get data to return
    data['injuries'] = matchOrSegmentResults.flatMap(matchOrSegment => matchOrSegment['unavailable'] || [])
                           .filter(unavailable => unavailable['type'] === 'injury');
    data['availableSuperstars'] = eventResults['availableSuperstars'] || [];
    data['facesOfTheCompanyMade'] = matchOrSegmentResults.flatMap(matchOrSegment => matchOrSegment['fotcChanges'] || [])
                                        .filter(fotc => !fotc['faceOfTheCompanyStart'] && fotc['faceOfTheCompany'])
                                        .map(fotc => fotc['name']);
    data['facesOfTheCompanyLost'] = matchOrSegmentResults.flatMap(matchOrSegment => matchOrSegment['fotcChanges'] || [])
                                        .filter(fotc => !fotc['faceOfTheCompanyStart'] && !fotc['faceOfTheCompany'])
                                        .map(fotc => fotc['name']);
    data['streaksEnded'] = matchOrSegmentResults.flatMap(matchOrSegment => matchOrSegment['streaks'] || [])
                               .filter(streak => streak['ended']);
    data['leftCompany'] = eventResults['leftCompany'] || [];
    data['signings'] = eventResults['signings'] || [];
    data['offersAccepted'] = eventResults['offersAccepted'] || [];
    data['fired'] = matchOrSegmentResults.flatMap(matchOrSegment => matchOrSegment['fired'] || []);
    data['feudsEnded'] = matchOrSegmentResults.flatMap(matchOrSegment => matchOrSegment['feudsEnded'] || []);
    data['hotChanges'] = matchOrSegmentResults.flatMap(matchOrSegment => matchOrSegment['hotChanges'] || []);
    data['staleChanges'] = matchOrSegmentResults.flatMap(matchOrSegment => matchOrSegment['staleChanges'] || []);
    data['debutedGimmicks'] = matchOrSegmentResults.flatMap(matchOrSegment => matchOrSegment['debutedGimmicks'] || []);

    return data;
}

async function simulateCompetitors({simulating = false} = {}) {  // simulate_competitors
    let data = {};
    let game = await getCurrentGame();
    let mainCompany = await getGameMainUserCompany(game);
    let mainCompanyEvents = (await getCompanyEvents(mainCompany)).reverse().filter(x => !x.temporaryEvent);
    let indieRoster = await getCompanyRosterByType(mainCompany, 'indie');
    let indieAvailableRoster = await getCompanyRosterByType(mainCompany, 'indieAvailable');
    let previousEvent = mainCompanyEvents[1];
    let latestEvent = mainCompanyEvents[0];
    let competitors = await getCpuControlledCompanies(mainCompany, game);
    let userCompanies = await getUserControlledCompanies(game);

    try {
        // Run simulation at end of month
        if (!game.monthlySimulationRun) {

            // Get maximum brand index of user controlled companies
            let [userBrandIndex, userRosterBrands] = await getUserRosterBrandsAndIndex(userCompanies);

            // Go through other companies latest event
            await asyncFor(competitors, async (competitor) => {
                try {
                    let event = await getCompanyLatestEvent(competitor);
                    let competitorMainRoster = await getCompanyRosterByType(competitor, 'main');
                    let competitorRosterBrands = await getRosterBrands(competitorMainRoster);

                    // Skip competitor show if no brand present corresponding to user brand
                    let skipCompetitorCheck = checkSkipCompetitorSimulation(userBrandIndex, competitorRosterBrands);
                    if (!skipCompetitorCheck) {
                        // Simulate event if same time as game's event
                        let eventIndex = (await getCompanyParentEvents(competitor)).map(x => x.name).indexOf((await getParentEvent(event))?.name);
                        let previousEventIndex = (await getCompanyParentEvents(mainCompany)).map(x => x.name).indexOf((await getParentEvent(previousEvent))?.name);
                        if ((event.weeklyShow === previousEvent.weeklyShow && event.weekNumber === previousEvent.weekNumber) &&
                            (event.weeklyShow || eventIndex === previousEventIndex)) {  // checks if at different monthly event
                            let simError = false;

                            try {
                                await simulateCompetitorEvent(event, competitorMainRoster, competitor, indieRoster,
                                    indieAvailableRoster, game);
                            } catch (error) {
                                captureError(error)
                                simError = true;
                                data['error'] = 'simulate-competitor-event';
                            }

                            // If user is on last brand show and competitor has more brand shows, simulate them until end
                            let simulateNextEventCheck = checkNextCompetitorEventSimulation(event, userBrandIndex,
                                userRosterBrands, competitorRosterBrands);
                            if (simulateNextEventCheck) {
                                let latestCompetitorEvent = await getCompanyLatestEvent(competitor);
                                let competitorBrandIndex = getBrandIndexForWeeklyShow(latestCompetitorEvent,
                                    competitorRosterBrands);

                                // Simulate next brand event until monthly event or first brand event
                                while (latestCompetitorEvent.weeklyShow && competitorBrandIndex > 0) {
                                    try {
                                        await simulateCompetitorEvent(latestCompetitorEvent, competitorMainRoster, competitor,
                                            indieRoster, indieAvailableRoster, game);
                                    } catch (error) {
                                        captureError(error)
                                        simError = true;
                                        data['error'] = 'simulate-competitor-event';
                                        break;
                                    }
                                    latestCompetitorEvent = await getCompanyLatestEvent(competitor);
                                    competitorBrandIndex = getBrandIndexForWeeklyShow(latestCompetitorEvent,
                                        competitorRosterBrands);
                                }
                            }

                            if (simError) {
                                // Adds next event to competitors
                                await fixCompetitorSimError(competitor, latestEvent, mainCompany, competitorRosterBrands);
                            }
                        }
                        // Otherwise, add event that is same time as game's latest event
                        else {
                            await fixCompetitorSimError(competitor, latestEvent, mainCompany, competitorRosterBrands);
                        }
                    }
                }
                catch (error) {
                    captureError(error)
                    data['error'] = 'simulate-competitor-event';
                }
            });

            // Get news
            let newsItems = [];
            if (!previousEvent.weeklyShow) {
                let allNewsItems = await getCompanyNewsItems(mainCompany);
                newsItems = allNewsItems.filter(x => x.year === game.newsYear && x.monthCount === game.newsMonth);

                // Remove previous news
                try {
                    let currentTotalMonths = game.newsMonth + (game.newsYear * 12);
                    for (let x of allNewsItems) {
                        let totalMonths = x.monthCount + (x.year * 12);
                        if (totalMonths < currentTotalMonths && !objectInArray(x, newsItems)) {
                            NewsItems.remove(x);
                        }
                    }
                } catch (err) {
                    captureError(err)
                }
            }

            // Get most important news items, sorting by headline and priority
            data['news'] = [];
            newsItems.sort((a, b) => b.headline - a.headline || b.priority - a.priority);
            for (let x of newsItems.slice(0, 16)) {
                data['news'].push(getNewsData(x));
            }

            game.monthlySimulationRun = true;

            // Get viewership data for all companies
            if (game.marketSize != null) {
                let allCompanies = await getGameActiveCompanies(game);
                data['fanChangeData'] = await getViewershipData(allCompanies, game, previousEvent);
            }
        }

        if (!simulating) {
            db.saveDatabase();
        }
    }
    catch (error) {
        captureError(error)
        data['error'] = 'simulate-competitors';
    }

    return data;
}

async function fixCompetitorSimError(competitor, latestEvent, mainCompany, competitorRosterBrands) {
    let newEvent;
    if (!latestEvent.weeklyShow) {
        let eventNames = (await getCompanyEvents(mainCompany)).slice(0, 12).map(x => x.name);
        let eventIndex = eventNames.indexOf(latestEvent.name);
        let parentEvent = (await getCompanyEvents(competitor))[eventIndex];
        if (!parentEvent && typeof window.Sentry != 'undefined') {
            Sentry.captureMessage('Parent event not found for competitor. eventIndex: ' + eventIndex + ' competitorEvents: ' + (await getCompanyEvents(competitor)).length)
        }
        newEvent = Event(parentEvent.name);
        await Events.insert(newEvent);
        await addChildEvent(parentEvent, newEvent);
    } else {
        let eventNames = (await getCompanyEvents(mainCompany)).slice(0, 12).map(x => x.name);
        let eventIndex = eventNames.indexOf(latestEvent.monthlyEventName);
        let monthlyEvent = (await getCompanyEvents(competitor))[eventIndex];

        // Add weekly show
        let weeklyShowName = competitor.weeklyShowName;
        if (!weeklyShowName) {
            weeklyShowName = `${formatCompanyNameBackend(competitor.name)} Weekly`;
        }
        let firstBrand = competitorRosterBrands.length > 0 ? competitorRosterBrands[0] : null;
        newEvent = await addWeeklyShow(weeklyShowName, monthlyEvent.name, latestEvent.weekNumber,
            firstBrand);
    }

    await addCompanyEvents(competitor, [newEvent]);
    competitor.realYear = mainCompany.realYear;
}

function checkSkipCompetitorSimulation(gameBrandIndex, competitorRosterBrands) {
    // Check if competitor has fewer brands than user's current brand
    return gameBrandIndex > 0 && competitorRosterBrands.length <= gameBrandIndex;
}

function checkNextCompetitorEventSimulation(latestEvent, gameBrandIndex, gameRosterBrands, competitorRosterBrands) {
    // Check if user is on its last brand's weekly show and competitor has more brand shows
    if (latestEvent.weeklyShow && (gameBrandIndex === null || gameBrandIndex + 1 >= gameRosterBrands.length)) {
        if (competitorRosterBrands.length > 1 &&
            (gameBrandIndex === null || competitorRosterBrands.length > gameBrandIndex + 1)) {
            return true;
        }
    }
    return false;
}

async function getUserRosterBrandsAndIndex(companies) {  // get_user_roster_brands_and_index
    // Get user company with highest brand index and number of brands
    let data = [];
    for (const company of companies) {
        let latestEvent = (await getCompanyEvents(company)).filter(x => x.rating !== null).reverse()[0];
        let mainRoster = await getCompanyRosterByType(company, 'main');
        let rosterBrands = await getRosterBrands(mainRoster);
        let brandIndex = getBrandIndexForWeeklyShow(latestEvent, rosterBrands);
        data.push([brandIndex, rosterBrands]);
    }
    return data.reduce((max, cur) => (cur[0] > max[0] || (cur[0] === max[0] && cur[1].length > max[1].length)) ?
        cur : max);
}

function getNewsData(news) {  // get_news_data
    let data = {};
    data['title'] = news.title;
    data['content'] = news.content;
    data['priority'] = news.priority;
    data['headline'] = news.headline;
    if (news.companyName) {
        data['company'] = news.companyName;
    }
    return data;
}

async function simulateCompetitorEvent(event, mainRoster, company, indieRoster, indieAvailableRoster, game) {
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const rosterBrands = await getRosterBrands(mainRoster);
    const companyEvents = (await getCompanyEvents(company)).reverse();
    const staffMembers = await getCompanyStaff(company);
    const producers = getAvailableStaffMembersByRole(staffMembers, 'producer');
    const parentEvents = await getCompanyParentEvents(company);
    const parentEvent = await getParentEventForEvent(parentEvents, event);
    const companyChampionships = await getCompanyChampionships(company);
    const mainCompany = await getGameMainUserCompany(game);
    const user = loadUser();

    // Normal difficulty
    let style = weightedChoice([['quality', 50], ['long-term', 30], ['standard', 20]]);
    let effect = null;
    let eventHypeBoost = 0;

    // Hard difficulty
    if (game.settings['competitor_difficulty'] === 'hard') {
        style = weightedChoice([['quality', 90], ['long-term', 10]]);
        effect = weightedChoice([['positive_boost', 80], [null, 20]]);
        eventHypeBoost = weightedChoice([[30, 90], [0, 10]]);
    }

    // Add positive/negative effects
    if (company['activeEffect'] === 'good_booking') {
        style = 'quality';
        effect = 'positive_boost';
    } else if (company['activeEffect'] === 'bad_booking') {
        style = 'terrible';
        effect = 'negative_boost';
    }

    // Auto book matches and segments
    let autoBookData = await autoBookEvent({company, roster: mainRoster, rosterSuperstars, latestEvent: event, producers,
        game, style, championships: companyChampionships, parentEvents, companyEvents});
    let bookedSegments = autoBookData['bookedSegments'];
    let bookedSegmentsAndMatches = autoBookData['bookedMatches'];
    if (bookedSegments) {
        bookedSegmentsAndMatches.push(...bookedSegments)
    }
    bookedSegmentsAndMatches.sort((a, b) => a.order - b.order);

    // Set event hype
    let topEvent = event.name === getTopEvent(parentEvents).name;
    event.hype = await getEventHype(event, topEvent, bookedSegmentsAndMatches, companyEvents[1], rosterBrands);

    // Add event hype boost
    if (company.activeEffect === 'good_booking') {
        eventHypeBoost = 30;
    } else if (company.activeEffect === 'bad_booking') {
        eventHypeBoost = 0;
    }
    event.hype += eventHypeBoost;

    // Set poster graphic
    if (!event.weeklyShow && globalUser.paid) {
        let posterData = await getEventPosterGraphicData(event, bookedSegmentsAndMatches, companyChampionships,
            parentEvent);
        if (posterData) {
            event.posterGraphic = posterData['graphics'];
        }
    }

    // Play matches and segments
    for (let matchOrSegment of bookedSegmentsAndMatches) {
        try {
            if (matchOrSegment.objectName === 'segment') {
                await processPlaySegment(matchOrSegment, null, bookedSegments, event, company, mainCompany,
                    game, mainRoster, indieRoster, indieAvailableRoster, null, rosterSuperstars, companyChampionships,
                    staffMembers, [...bookedSegmentsAndMatches]);
            }
            else {
                let winner = await simulateMatchWinner(matchOrSegment, style);
                let matchFinish = await getMatchFinishForSimulatorMatch(matchOrSegment, event, company);
                await processPlayMatch(matchOrSegment, winner.name, event, company, mainCompany, mainRoster,
                    indieRoster, game, {}, matchFinish, null, rosterSuperstars, parentEvents, companyChampionships,
                    staffMembers, [...bookedSegmentsAndMatches], effect, true);
            }
        }
        catch (error) {
            captureError(error)
        }
    }

    // End event, run post event
    await endEvent(company, user, true, rosterSuperstars);
    if (!event.weeklyShow) {
        await simulatePostEvent(company, user, style, 'manage');
    }

    return true;
}

async function getMatchFinishForSimulatorMatch(match, event, company) {
    let matchFinish = {};

    let addMatchFinishChance = 5;
    if (event && event.weeklyShow) {
        addMatchFinishChance = 20;
    }

    // Chance to add match finish
    if (getRandomInt(0, 100) <= addMatchFinishChance) {

        // Get non-special finishes that can be used
        let availableMatchFinishes = matchFinishesData.filter(x => x['category'] !== 'special'
            && company.points >= x['cost']);

        // Remove indecisive finishes if gimmick match
        if (await getMatchGimmickMatch(match)) {
            availableMatchFinishes = availableMatchFinishes.filter(x => x['changeChampionship']);
        }

        // Choose random finish
        if (availableMatchFinishes.length > 0) {
            matchFinish = getRandomChoice(availableMatchFinishes);

            // Subtract points
            company.points -= matchFinish['cost'];
        }
    }

    return matchFinish;
}

async function simulatePostEvent(company, user, style = 'standard', simulatorOption = 'manage') {  // simulate_post_event
    let data = {};
	const game = await getCurrentGame();
	const mainCompany = await getGameMainUserCompany(game);
    const companyName = company.name;
    const month = game.monthCount;
    const year = game.year;
    const companyMonth = getCompanyMonth(company, game);
    const companyYear = getCompanyYear(company, game);
    const powerups = await getCompanyPowerups(company);
    let retired = [];
    let awards = [];
    let reviewData = {};

    // Introduce powerups and special segments
    if (!company.userControlled || !game.settings['unlock_all_items']) {
        if (month === 1 && year === 0) {
            await introducePowerups(company);
        }
        if (month === 4 && year === 0) {
            await introduceSpecialSegments(company, game, user);
        }
    }

    // Process user company
    if (company.userControlled) {
        // Yearly updates
        if (companyMonth === 0 && companyYear > 0) {
            const yearlyData = await processSuperstarsYearlyChanges(company, true);
            retired = yearlyData['retired'];
        }

        // Objectives review and messages
        reviewData = await reviewObjectives(company, game);
        let objectivesMessage = await checkToAddCurrentObjectivesMessage(company);
        if (objectivesMessage) {
            await addCompanyMessages(company, [objectivesMessage]);
        }

        // Awards
        if (month === 10) {
            const awardsData = await processAwards();
            if (awardsData['awards']) {
                awards = awardsData['awards'];
            }
        }
    }

    // Simulate actions
    let createdTagteams = [];
    let createdTrios = [];
    let createdFactions = [];
    let disbandedTagteams = [];
    let createdChampionships = [];
    let callups = [];
    let powerupsUsed = [];
    let releasedSuperstars = [];
    let managersTransitioned = [];
    let managersAdded = [];
    let managersRemoved = [];
    let brandsChanged = [];

	// Manage company
	if (simulatorOption === 'manage') {
	    let companyRosters = await getCompanyRosters(company);
	    let mainRoster = await getCompanyRosterByType(company, 'main', companyRosters);
	    let trainingRoster = await getCompanyRosterByType(company, 'training', companyRosters);
	    let trainingAvailableRoster = await getCompanyRosterByType(company, 'trainingAvailable', companyRosters);
	    let indieRoster = await getCompanyRosterByType(mainCompany, 'indie');
	    let indieAvailableRoster = await getCompanyRosterByType(mainCompany, 'indieAvailable');
	    let rosterSuperstars = await getRosterSuperstars(mainRoster);
	    let rosterTagTeams = await getRosterTagTeams(mainRoster);
	    let rosterTagTeamSuperstars = (await Promise.all(rosterTagTeams.filter(x => !x.trio).map(t => getTagTeamSuperstars(t)))).flat();
	    let rosterTrioSuperstars = (await Promise.all(rosterTagTeams.filter(x => x.trio).map(t => getTagTeamSuperstars(t)))).flat();
	    let championships = await getCompanyChampionships(company);
	    let championshipSuperstarIds = championships.map(x => x.superstarId);
	    let rosterBrands = await getRosterBrands(mainRoster);

        // Get chances
	    let tagTeamChance = 6;
	    if (style === 'long-term') {
	        tagTeamChance = 3;
	    } else if (style === 'bizarro') {
	        tagTeamChance = 15;
	    }
	    let factionsChance = 20;
	    let signingChance = 50;
	    let championshipChance = 1;
	    if (style === 'long-term') {
	        championshipChance = 0;
	    } else if (style === 'bizarro') {
	        championshipChance = 4;
	    }
	    let gimmickChance = 20;
	    if (style === 'long-term') {
	        gimmickChance = 10;
	    } else if (style === 'bizarro') {
	        gimmickChance = 40;
	    }
		let powerupsChance = 80;

        // Callup
		if (trainingRoster && (companyMonth === 1 || companyMonth === 7)) {
            // Add to available training roster
            if (!company.trainingTransferRun) {
                const talentToTransfer = getRandomChoice(await getRosterSuperstars(trainingRoster));
                if (talentToTransfer) {
                    await transferTalentToRoster(talentToTransfer, trainingAvailableRoster);
                }
                company.trainingTransferRun = true;
            }

            // Run callup
            if (!company.trainingCallupRun) {
                let trainingRosterSuperstars = await getRosterSuperstars(trainingAvailableRoster);
                if (trainingRosterSuperstars.length) {

                    // Get talent to callup
                    let superstar = getRandomChoice(trainingRosterSuperstars);
                    let superstarTagTeam = (await getSuperstarTagTeams(superstar)).find(x => x);
                    let talent = superstarTagTeam || superstar;

                    // Callup, mark as done
                    await callupTalentToMainRoster(talent, mainRoster, company, game);
                    company.trainingCallupRun = true;
                    callups.push(talent.name);

                    // Increase morale for talent superstars
                    for (const x of (await getRosterMemberSuperstars(talent))) {
                        updateSuperstarMorale(x, getRandomInt(20, 25));
                    }
                }
            }
		}

		// Disband tag team
		if (getRandomInt(0, 100) <= tagTeamChance / 1.5) {

            // Get non champions
		    let nonChampionTagTeams = [];
            for (const x of rosterTagTeams) {
                if (!(await getTagTeamSuperstars(x)).some(s => championshipSuperstarIds.includes(s.$loki))) {
                    nonChampionTagTeams.push(x)
                }
            }

		    if (nonChampionTagTeams.length) {
		        let tagTeam = weightedChoice(await getDisbandTagTeamWeights(nonChampionTagTeams));
                let tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);

                // Check talent request
		        for (let superstar of tagTeamSuperstars) {
                    let requestData = {'teamId': tagTeam.$loki};
                    await checkTalentRequestAction({company: company, requestType: 'disband_team', talent: superstar,
                        data: requestData});
		        }

                await removeTagTeam(tagTeam);
		        disbandedTagteams.push(tagTeam.name);
		    }
		}

		// Add tag team
		if (getRandomInt(0, 100) <= tagTeamChance) {
		    let tagTeamType = (getRandomInt(0, 100) <= 65) ? 'tagTeam' : 'trio';
		    if (tagTeamType === 'tagTeam') {
		        let availableSuperstars = rosterSuperstars.filter(x => !objectInArray(x, rosterTagTeamSuperstars) &&
                    !x.retired && !x.fullTimeManager);
		        if (availableSuperstars.length > 1) {
		            let superstarWeights = getAddTagTeamWeights(availableSuperstars, style);
		            let firstSuperstar = weightedChoice(superstarWeights);

                    // Update weights for close popularity, gender, firstSuperstar
		            let secondSuperstarWeights = superstarWeights.filter(y => y[0].gender === firstSuperstar.gender &&
                        y[0] !== firstSuperstar && (!y[0].brandName || y[0].brandName === firstSuperstar.brandName))
                        .map(y => addTagteamPopularityCheck(y, firstSuperstar, style));
		            if (secondSuperstarWeights.length) {
		                let secondSuperstar = weightedChoice(secondSuperstarWeights);
		                let tagTeamMembers = [firstSuperstar, secondSuperstar];
		                let newTagTeam = await generateTagTeam(tagTeamMembers);
		                await addRosterTagTeams(mainRoster, [newTagTeam]);
		                createdTagteams.push({'name': newTagTeam.name, 'superstars': tagTeamMembers.map(x => x.name)});

                        // Check talent request
		                for (let participant of tagTeamMembers) {
		                    let teamMember = tagTeamMembers.find(x => x !== participant);
		                    if (teamMember) {
		                        let requestData = {'talentId': teamMember.$loki};
                                await checkTalentRequestAction({company: company, requestType: 'team_with_x',
                                    talent: participant, data: requestData});
		                    }
		                }
		            }
		        }
		    } else if (tagTeamType === 'trio') {
		        let availableSuperstars = rosterSuperstars.filter(x => !objectInArray(x, rosterTrioSuperstars) &&
                    !x.retired && !x.fullTimeManager);
		        if (availableSuperstars.length > 2) {
		            let superstarWeights = getAddTagTeamWeights(availableSuperstars, style);
		            let firstSuperstar = weightedChoice(superstarWeights);
                    superstarWeights = superstarWeights.filter(y => y[0].gender === firstSuperstar.gender &&
                        y[0] !== firstSuperstar && (!y[0].brandName || y[0].brandName === firstSuperstar.brandName))
                        .map(y => addTagteamPopularityCheck(y, firstSuperstar, style));

		            if (superstarWeights.length > 1) {
		                let secondSuperstar = weightedChoice(superstarWeights);
		                superstarWeights = superstarWeights.filter(x => x[0] !== secondSuperstar);
		                let thirdSuperstar = weightedChoice(superstarWeights);
                        let trioSuperstars = [firstSuperstar, secondSuperstar, thirdSuperstar];
		                let newTrio = await generateTrio(trioSuperstars);
		                await addRosterTagTeams(mainRoster, [newTrio]);
		                createdTrios.push({'name': newTrio.name,
                            'superstars': trioSuperstars.map(x => x.name)});

                        // Check talent request
		                for (let participant of trioSuperstars) {
		                    for (const teamMember of trioSuperstars.filter(x => x !== participant)) {
		                        let requestData = {'talentId': teamMember.$loki};
                                await checkTalentRequestAction({company: company, requestType: 'team_with_x',
                                    talent: participant, data: requestData});
		                    }
		                }
		            }
		        }
		    }
		}

		// Release superstars if roster above capacity
		let capacity = getCompanyRosterCapacity(company, game);
		let size = rosterSuperstars.filter(x => !x.retired && !x.dead).length;
        while (size > capacity && getRandomInt(0, 100) <= 50) {
            let availableSuperstars = rosterSuperstars.filter(x => !championshipSuperstarIds.includes(x.$loki) &&
                x.activeEffect !== 'traded' && !x.retired && x.name !== company.ownerName);
            if (availableSuperstars.length) {
                let superstarToRelease;
                if (style === 'bizarro') {
                    superstarToRelease = getRandomChoice(availableSuperstars);
                } else {
                    superstarToRelease = getArrayMinByProperty(availableSuperstars, 'popularity');
                }

                // Add to indie roster
                rosterSuperstars = rosterSuperstars.filter(x => x !== superstarToRelease);
                if (game.settings['all_indies_available']) {
                    await addRosterSuperstars(indieAvailableRoster, [superstarToRelease])
                } else {
                    await addRosterSuperstars(indieRoster, [superstarToRelease])
                }
                releasedSuperstars.push(superstarToRelease.name);

                // Remove teams/managers/factions
                await removeSuperstarTagTeams(superstarToRelease);
                await removeSuperstarFactionRelationships(superstarToRelease, month, year);
                await removeSuperstarManagerRelationships(superstarToRelease);

                // Delete all relationships, add former company relationship
                for (const relationship of (await getSuperstarRelationships(superstarToRelease))) {
                    await Relationships.remove(relationship)
                }
                await addFormerCompanyRelationship(superstarToRelease, company);

                // Delete talent requests
                for (const talentRequest of (await getSuperstarTalentRequests(superstarToRelease))) {
                    await TalentRequests.remove(talentRequest)
                }

                // Make superstar happy
                changeSuperstarMoraleToHappy(superstarToRelease);

                // Decrease overness
                superstarToRelease.overness -= getRandomInt(8, 12);

                // Reset attributes
                superstarToRelease.brandName = null;
                superstarToRelease.faceOfTheCompanyCounter = 0;
                superstarToRelease.suspensionMonths = 0;

                // Add news
                let newsItem = await generateNewsItem({
                    company, month: game.newsMonth, year: game.newsYear, superstar: superstarToRelease,
                    newsType: 'superstar_released_by_company'
                });
                await addCompanyNewsItems(mainCompany, [newsItem]);

                size = rosterSuperstars.filter(x => !x.retired && !x.dead).length;
            }
        }

		// Negotiate with talent
		if (getRandomInt(0, 100) <= signingChance) {

		    let competitors = await getActiveCompetitorsForCompany(company, game);

		    // Pick number of offers to make
		    let numberOfOffers = weightedChoice([[1, 80], [2, 15], [3, 5]]);
		    for (let y = 0; y < numberOfOffers; y++) {
		        if (company.points > 100 && size < capacity) {

		            // Choose roster
                    let selectedCompetitor;
		            let offerRoster = null;
		            if (getRandomInt(0, 100) <= 60 && (await getRosterSuperstars(indieAvailableRoster)).length) {
		                offerRoster = indieAvailableRoster;
		            }
                    else if (competitors.length) {
		                // Choose roster from competitors, double chance to choose main company
                        let competitorWeights = competitors.map(y => y === mainCompany ? [y, y.reputation * 2] :
                            [y, y.reputation]);
		                selectedCompetitor = weightedChoice(competitorWeights);
		                let offerCompanyRosters = await getCompanyRosters(selectedCompetitor);
                        let offerCompanyMainRoster = await getCompanyRosterByType(selectedCompetitor, 'main',
                            offerCompanyRosters);
                        let offerCompanyTrainingRoster = await getCompanyRosterByType(selectedCompetitor, 'training',
                            offerCompanyRosters);
                        offerRoster = offerCompanyMainRoster;
                        // Small chance to choose training roster
		                if (getRandomInt(0, 100) <= 20 && offerCompanyTrainingRoster &&
                            (await getRosterSuperstars(offerCompanyTrainingRoster)).length) {
		                    offerRoster = offerCompanyTrainingRoster;
		                }
		            }

		            if (offerRoster) {
		                let isContracted = (offerRoster !== indieAvailableRoster);
		                let runningNegotiations = await getCompanySentNegotiations(company);

		                // Choose superstars or teams
		                let runningNegotiationsIds, availableTalent;
                        let offerRosterTagTeams = await getRosterTagTeams(offerRoster);
		                if (getRandomInt(0, 100) <= 20 && offerRosterTagTeams.length) {
		                    runningNegotiationsIds = runningNegotiations.map(x => x.tagTeamId);
		                    availableTalent = offerRosterTagTeams.filter(x => !x.temporary);
		                } else {
		                    runningNegotiationsIds = runningNegotiations.map(x => x.superstarId);
		                    let offerRosterTagTeamSuperstars = (await Promise.all(offerRosterTagTeams
                                .map(t => getTagTeamSuperstars(t)))).flat();
		                    availableTalent = (await getRosterSuperstars(offerRoster))
		                        .filter(x => !objectInArray(x, offerRosterTagTeamSuperstars));
		                }

                        // Get talent available to send offer to, check for already running negotiations, trade deals etc
                        let availableTalentFiltered = [];
                        for (const x of availableTalent) {
                            let s = await getRosterMemberSuperstars(x);
                            if (!runningNegotiationsIds.includes(x.$loki) &&
                                company.points >= await getTalentValueAmount(x, company, isContracted) &&
                                !s.some(y => y.activeEffect === 'traded' || y.availableThisMonth)) {
                                availableTalentFiltered.push(x)
                            }
                        }

		                if (availableTalentFiltered.length) {

		                    // Choose talent, make offer
		                    let talentAvailableWeights = await getNegotiationWeights(availableTalentFiltered, style);
		                    let negotiationTalent = weightedChoice(talentAvailableWeights);
		                    let newNegotiation = await makeTalentOffer(negotiationTalent, company, selectedCompetitor);

		                    // Chance for cpu competitors to make offer to talent too if they can afford it
		                    if (getRandomInt(0, 100) <= 30) {
		                        let cpuCompetitors = competitors.filter(x => !x.userControlled && x !== selectedCompetitor);
		                        if (cpuCompetitors.length) {
                                    // Limit to 3 offer companies
                                    let numberOfOfferCompaniesMax = cpuCompetitors.length > 3 ? 3 : cpuCompetitors.length;
		                            let cpuCompetitorsSample = getRandomSample(cpuCompetitors,
                                        getRandomInt(1, numberOfOfferCompaniesMax));
		                            for (let cpuCompetitor of cpuCompetitorsSample) {
		                                if (cpuCompetitor.points > newNegotiation.offeredAmount) {
		                                    await makeTalentOffer(negotiationTalent, cpuCompetitor, selectedCompetitor);
		                                }
		                            }
		                        }
		                    }
		                }
		            }
		        }
		    }
		}

		// Retire championship
		if (getRandomInt(0, 100) <= championshipChance) {
		    // Don't retire if company has 2 or fewer championships
		    if (getUniqueChampionships(championships).length > 2) {
		        let championshipWeights = getRetireChampionshipWeights(championships);
		        let championship = weightedChoice(championshipWeights);

		        // Add points
		        let points = getChampionshipCost(championship.rank, true, championship.gimmickRestriction);

                // Remove championship(s)
                let championshipsToRetire = championships.filter(x => checkAreChampionshipsEqual(x, championship));
                for (const x of championshipsToRetire) {
                    championships = championships.filter(c => c !== x);
                    const champion = await getChampionshipSuperstar(x);
                    if (champion) {
                        await removeSuperstarChampionships(champion, [x]);
                        champion.popularity -= getChampionshipPopularityDecrease(x);
                        const oldReign = await getChampionshipCurrentReign(x);
                        if (oldReign) {
                            oldReign.endMonth = month;
                            oldReign.endYear = year;
                            oldReign.retired = true;
                        }
                    }
                    if (x.matchContract) {
                        const matchContract = await getChampionshipMatchContract(x);
                        if (matchContract) {
                            MatchContracts.remove(matchContract)
                        }
                    }
                    if (x.matchContract || x.championshipContract) {
                        points = 0;
                        await removeCompanyChampionships(company, [x]);
                    }
                    x.retired = true;
                }

		        company.points += points;
		    }
		}

		// Vacate championships if champion unavailable
		for (const championship of championships) {
		    let champion = await getChampionshipSuperstar(championship);
		    let femaleNames = ['women', 'knockout', 'diva', 'female', 'ladies', 'woman'];
		    let wrongPronounChampion = femaleNames.some(n => championship.name.toLowerCase().includes(n)) && champion &&
		                               champion.gender === 'male';
		    if ((champion && !checkAvailability(champion, true)) || wrongPronounChampion) {
		        let allChampionships = championships.filter(x => checkAreChampionshipsEqual(x, championship));
		        for (const c of allChampionships) {
		            c.vacated = true;
		            let holder = await getChampionshipSuperstar(c);
		            if (holder) {
		                await removeSuperstarChampionships(holder, [c]);
		            }
		            const oldReign = await getChampionshipCurrentReign(c);
                    if (oldReign) {
                        oldReign.endMonth = month;
                        oldReign.endYear = year;
                        oldReign.vacated = true;
                    }

		            // Check talent request
		            if (holder) {
		                let requestData = {'championshipId': c.$loki};
                        await checkTalentRequestAction({company: company, requestType: 'vacate_championship',
                            talent: holder, data: requestData});
		            }
		        }
		    }
		}

		// Faction actions
		if (getRandomInt(0, 100) <= factionsChance && globalUser.paid) {
		    let rosterFactions = await getRosterActiveFactions(mainRoster);
		    let rosterFactionSuperstars = (await Promise.all(rosterFactions.map(x => getFactionSuperstars(x)))).flat();
		    let superstarsAvailableForFactions = rosterSuperstars.filter(x => !objectInArray(x, rosterFactionSuperstars));

		    // Faction actions if factions present or at limit
		    if (rosterFactions.length && (getRandomInt(0, 100) <= 75 || rosterFactions.length >= 8)) {
		        let factionActionPick = getRandomInt(0, 100);
		        let factionForAction = getRandomChoice(rosterFactions);
		        let factionSuperstars = await getFactionSuperstars(factionForAction);

		        // Chance to remove members
		        if (factionActionPick <= 40) {
		            if (factionSuperstars.length > 2) {
		                let memberToRemove = getRandomChoice(factionSuperstars);
                        await removeSuperstarsFromFaction(factionForAction, [memberToRemove]);
		                // Don't add reputation change to give AI a chance at awards

                        // Check leave faction request
                        let requestData = {'factionId': factionForAction.$loki};
                        await checkTalentRequestAction({company: company, requestType: 'leave_faction',
                            talent: memberToRemove, data: requestData});
		            }
		        }
                // Chance to add members
                else if (factionActionPick <= 80) {
		            if (factionSuperstars.length < gameLimits.maxFactionSuperstars) {
		                let memberToAdd = getRandomChoice(superstarsAvailableForFactions);
		                let membersToAdd = [memberToAdd];
		                await processFactionXpChangeForFaction({faction: factionForAction, addedMembers: membersToAdd});
		                await addFactionSuperstars(factionForAction, membersToAdd);

                        // Check join faction request
                        let requestData = {'factionId': factionForAction.$loki};
                        await checkTalentRequestAction({company: company, requestType: 'join_faction',
                            talent: memberToAdd, data: requestData});
		            }
		        }
                // Chance to change leader
		        else if (factionActionPick <= 90) {
		            let newLeader = getRandomChoice(factionSuperstars);
		            await setFactionLeaderSuperstar(factionForAction, newLeader);
		        }
                // Chance to disband
		        else if (factionActionPick <= 100) {
		            await disbandFaction(factionForAction, month, year);
		        }
		    }
            // Chance to add faction if superstars available and not at factions limit
		    else if (superstarsAvailableForFactions.length > 8 && rosterFactions.length < 8) {

		        // Create faction and add to roster
		        let factionSuperstars = getRandomSample(superstarsAvailableForFactions, getRandomInt(2, 8));
		        let newFaction = await generateFaction(factionSuperstars, rosterFactions, rosterTagTeams);
		        await addRosterFactions(mainRoster, [newFaction]);

		        // Add company log
                const newLog = CompanyLog(game.monthCount, company.realYear);
                CompanyLogs.insert(newLog);
                newLog.text = `${newFaction.name}`;
                newLog.type = 'new-faction';
                newLog.realYear = game.year;
                await addCompanyCompanyLogs(company, [newLog]);

		        createdFactions.push({'name': newFaction.name, 'superstars': factionSuperstars.map(x => x.name)});

		        // Check start faction request
		        for (let factionSuperstar of factionSuperstars) {
		            await checkTalentRequestAction({company: company, requestType: 'start_faction',
                        talent: factionSuperstar});
		        }
		    }
		}

		// Create championship
		if (getRandomInt(0, 100) <= championshipChance) {
		    let rankNameWeights = [['Main Eventer', 2], ['Upper Midcarder', 10], ['Lower Midcarder', 8], ['Jabroni', 2]];
		    let rankName = weightedChoice(rankNameWeights);
		    let cost = getChampionshipCost(rankName);

		    if (company.points > cost) {

		        // Chance to add women's championship
		        let femaleSuperstar = rosterSuperstars.filter(x => x.gender === 'female');
		        let isWomens = false;
		        if (femaleSuperstar.length > 4 && getRandomInt(0, 100) <= 30) {
		            isWomens = true;
		        }

		        // Generate unique name
		        let championshipName = generateChampionshipName(companyName, rankName, null, isWomens);
		        while (championships.some(x => x.name === championshipName)) {
		            championshipName = generateChampionshipName(companyName, rankName, null, isWomens);
		        }

                // Get number of championships to create
		        let division = weightedChoice([['singles', 70], ['tag-team', 20], ['trio', 10]]);
		        let divisionData = {'singles': 1, 'tag-team': 2, 'trio': 3};
		        let numberOfChampionships = divisionData[division] || 1;

		        // Create championships
		        let championshipGraphic = generateChampionshipGraphic();
		        for (let championship of Array(numberOfChampionships).keys()) {
		            const newChampionship = Championship(championshipName);
                    newChampionship.rank = rankName;
                    newChampionship.vacated = true;
		            if (division === 'tag-team') {
                        newChampionship.tagTeam = true;
                    }
                    if (division === 'trio') {
                        newChampionship.trio = true;
                    }
                    newChampionship.graphic = championshipGraphic;
                    Championships.insert(newChampionship);
                    await addCompanyChampionships(company, [newChampionship]);
		        }

		        // Change objective property
		        if (company.userControlled) {
                    const objectiveCompleted = await changeProperty({name: 'new championship', change: 1, company: company});
                    if (objectiveCompleted) {
                        await processObjectiveCompleted(objectiveCompleted, company);
                    }
		        }

		        // Subtract points
		        company.points -= cost;

		        createdChampionships.push(championshipName);
		    }
		}

		// Artifacts
		let unlockedPowerups = powerups.filter(x => x.unlocked);
		if (unlockedPowerups.length) {

		    // Buy artifact (if low on stock and can afford)
		    let availablePowerups = unlockedPowerups.filter(x => x.stock > 0);
		    if (getRandomInt(0, 100) <= powerupsChance / 5) {
		        let powerupsToBuy = unlockedPowerups.filter(x => company.points > x.cost && x.stock === 0);
		        if (powerupsToBuy.length && availablePowerups.length < 3) {
		            let powerup = getRandomChoice(powerupsToBuy);
		            company.points -= powerup.cost;
		            powerup.stock += 1;
		        }
		    }

		    if (game.settings['generate_indie_superstars'] === false) {
		        availablePowerups = availablePowerups.filter(x => x.effect !== 'recruitment');
		    }

		    // Use artifact
		    if (availablePowerups.length) {
                if (getRandomInt(0, 100) <= powerupsChance) {
                    // Get powerups that have selections available
                    let powerupsAndSelections = await Promise.all(availablePowerups.map(async x =>
                        [x, await getPowerupSelections(company, x)]));
                    powerupsAndSelections = powerupsAndSelections.filter(x => (x[1]['selections'] || !x[0].selection));
                    if (powerupsAndSelections.length) {
                        let powerupAndSelections = getRandomChoice(powerupsAndSelections);
                        let powerup = powerupAndSelections[0];
                        let selections = powerupAndSelections[1]['selections'];
		                if (powerup.selection) {
		                    let selection = getRandomChoice(selections);
		                    await applyPowerupToSelection(company, powerup, selection['uid']);
		                    powerupsUsed.push({'name': powerup.name, 'usedOn': selection['name']});
		                } else {
		                    await applyPowerupToSelection(company, powerup);
		                    powerupsUsed.push({'name': powerup.name});
		                }
		            }
		        }
		    }
		}

		// Gimmick
		if (getRandomInt(0, 100) <= gimmickChance) {

            // Get superstar and gimmick
		    let superstar = getRandomChoice(rosterSuperstars);
		    let superstarGimmick = await getSuperstarCurrentGimmick(superstar);
		    if (superstarGimmick) {
		        // Pick again, bias against superstars with gimmicks
		        superstar = getRandomChoice(rosterSuperstars);
		        superstarGimmick = await getSuperstarCurrentGimmick(superstar);
		    }

		    // Check for points if changing gimmick
		    let gimmickPointsCheck = false;
		    if (superstarGimmick && !superstarGimmick.nextGimmickBoost) {
		        if (company.points >= 30) {
		            gimmickPointsCheck = true;
		        }
		    } else {
		        gimmickPointsCheck = true;
		    }

		    if (gimmickPointsCheck) {
		        let character, personality, customGimmickName = null;

		        if (getRandomInt(0, 100) <= 20) {
		            customGimmickName = generateGimmickName();
		        } else {
		            let characters = charactersData.filter(x => x.standard);
		            if (globalUser.paid) {
                        characters.push(...charactersData.filter(x => !x.standard &&
                            user.unlockedCharacters.includes(x.name)));
		            }
		            character = getRandomChoice(characters)['name'];
		            personality = getRandomChoice(personalitiesData)['name'];
		        }

		        // Set gimmick name
		        let gimmickName;
		        if (customGimmickName) {
		            gimmickName = customGimmickName;
		        } else {
		            gimmickName = `${personality} ${character}`;
		        }

		        // Check usage of character
		        let alreadyUsedPenalty = 0;
		        if (character) {
		            const timesUsed = await checkCharacterUsage(character, mainRoster, superstar);
		            if (timesUsed > 0) {
		                if (timesUsed >= 8) {
		                    alreadyUsedPenalty = -40;
		                } else if (timesUsed >= 5) {
		                    alreadyUsedPenalty = -15;
		                } else if (timesUsed >= 2) {
		                    alreadyUsedPenalty = -5;
		                }
		            }
		        }

		        // Next gimmick available bonus
		        let nextGimmickBonus = 0;
		        if (superstarGimmick && superstarGimmick.nextGimmickBoost) {
		            nextGimmickBonus = 20;
		        }

		        // Create gimmick
		        const newGimmick = await createGimmick(superstar, gimmickName, character, alreadyUsedPenalty,
                    nextGimmickBonus);
		        superstar.debutNewGimmick = true;

		        // Get combination bonus
		        if (character && personality) {
                    newGimmick.combinationBonus = await getGimmickCreationBonus(character, personality);
		        }

		        // Remove points if changing gimmick
		        if (superstarGimmick && !superstarGimmick.nextGimmickBoost) {
		            company.points -= 30;
		        }
		    }
		}

		// Manager actions
		let transitionTalentChance = 15;
		let addManagerRelationshipChance = 10;
		let removeManagerRelationshipChance = 5;
		if (globalUser.paid) {
		    if (getRandomInt(0, 100) <= removeManagerRelationshipChance) {
		        // Get superstar from superstars with managers assigned
		        let superstarsWithManagers = rosterSuperstars.filter(x => x.managerId);
		        if (superstarsWithManagers.length) {
		            let superstarToRemove = getRandomChoice(superstarsWithManagers);
		            let manager = rosterSuperstars.find(x => x.$loki === superstarToRemove.managerId);
		            if (manager) {
		                // Remove superstar from manager
		                await removeSuperstarsFromManager(manager, [superstarToRemove]);
		                managersRemoved.push({'managerName': manager.name, 'removedSuperstarsNames': [superstarToRemove.name]});
		            }
		        }
		    }

		    if (getRandomInt(0, 100) <= addManagerRelationshipChance) {
		        // Get manager from full-time managers and talent with high charisma who are not managing someone
		        let managerIds = [...new Set(rosterSuperstars.filter(x => x.managerId).map(x => x.managerId))];
		        let managerChoices = rosterSuperstars.filter(x => x.fullTimeManager && x.charisma >= 2 &&
                    !managerIds.includes(x.$loki));
		        if (!managerChoices.length) {
		            managerChoices = rosterSuperstars.filter(x => x.charisma >= 2 && !managerIds.includes(x.$loki));
		        }
		        if (managerChoices.length) {
		            let manager = getRandomChoice(managerChoices);

		            // Get superstar from non full-time manager superstars without managers with charisma lower than manager's
		            let superstarToManageChoices = rosterSuperstars.filter(x => !x.fullTimeManager &&
                        x.$loki !== manager.$loki && x.charisma < manager.charisma && !x.managerId && !x.retired &&
                        !x.partTimer && (!x.brandName || x.brandName === manager.brandName));
		            if (superstarToManageChoices.length) {
		                let superstarToManage = getRandomChoice(superstarToManageChoices);

		                // Add superstar to manager
		                await addSuperstarsToManager(manager, [superstarToManage]);
		                managersAdded.push({'managerName': manager.name, 'managedSuperstarsNames': [superstarToManage.name]});

		                // Check manager requests
                        let requestData = {'talentId': superstarToManage.$loki};
                        await checkTalentRequestAction({company: company, requestType: 'be_managed',
                            talent: superstarToManage});
                        await checkTalentRequestAction({company: company, requestType: 'manage_x',
                            talent: manager, data: requestData});
		            }
		        }
		    }

		    if (getRandomInt(0, 100) <= transitionTalentChance) {
		        // Transition retired talent with high charisma into full-time managers
		        let superstarChoices = rosterSuperstars.filter(x => !x.fullTimeManager && x.retired && x.charisma >= 3);
		        if (superstarChoices.length) {
		            let superstarToTransition = getRandomChoice(superstarChoices);
		            superstarToTransition.fullTimeManager = true;
		            managersTransitioned.push(superstarToTransition.name);
		            let manager = await getSuperstarManager(superstarToTransition);
		            if (manager) {
		                await removeSuperstarsFromManager(manager, [superstarToTransition]);
		            }
		        }
		    }
		}

		// Brand actions
		if (globalUser.paid) {
		    let brandShuffleChance = 8;

		    // If more than one exclusive brand, shuffle superstars
		    let exclusiveRosterBrands = rosterBrands.filter(x => x.weeklyShowIsExclusive);
		    if (exclusiveRosterBrands.length > 1 && getRandomInt(0, 100) <= brandShuffleChance) {
		        let superstarsMoved = [];
		        for (let brand of exclusiveRosterBrands) {
		            let availableSuperstars = rosterSuperstars.filter(x => x.brandName !== brand.name &&
                        !championshipSuperstarIds.includes(x.$loki) && !objectInArray(x, superstarsMoved));
		            if (availableSuperstars.length > 6) {
		                // Move random superstars to this brand
		                let superstarsToMove = getRandomSample(availableSuperstars, getRandomInt(3, 6));
		                for (let superstarToMove of superstarsToMove) {
		                    draftSuperstarToBrand(superstarToMove, brand.name, championships);
		                    // Check talent request
		                    let requestData = {'brandId': brand.$loki};
                            await checkTalentRequestAction({company: company, requestType: 'change_brand',
                                talent: superstarToMove, data: requestData});
		                }
		                superstarsMoved.push(...superstarsToMove);
		                brandsChanged.push({'superstars': commaSeparate(superstarsToMove.map(s => s.name)),
                            'brand': brand.name});
		            }
		        }
		    }
		}

		// End feuds with low hype
		if (style === 'quality') {
		    for (let feud of (await getRosterFeuds(mainRoster))) {
		        if (feud.hype < 0) {
		            feud.ended = true;
		            feud.endMonth = month;
		            feud.endYear = year;

		            // For ending potential feuds
		            if (!feud.started) {
		                feud.rating = null;
		            } else {
		                feud.rating = await getFeudRating(feud);
		            }
		        }
		    }
		}
	}

    if (company.points < 0) {
        company.points = 0;
    }

    // Get data to return
    data['createdTagTeams'] = createdTagteams;
    data['disbandedTagTeams'] = disbandedTagteams;
    data['createdTrios'] = createdTrios;
    data['createdFactions'] = createdFactions;
    data['createdChampionships'] = createdChampionships;
    data['callups'] = callups;
    data['retired'] = retired;
    data['awardsWon'] = awards.filter(award => Object.keys(award['reward']).length !== 0);
    data['powerupsUsed'] = powerupsUsed;
    data['releasedSuperstars'] = releasedSuperstars;
    data['managersTransitioned'] = managersTransitioned;
    data['managersAdded'] = managersAdded;
    data['managersRemoved'] = managersRemoved;
    data['brandsChanged'] = brandsChanged;
    data['reviewData'] = reviewData;

    return data;
}

async function getDisbandTagTeamWeights(tagTeams) {  // disband_tagteam_weights
    let weights = [];
    for (let t of tagTeams) {
        let weight = Math.pow(1000 / await getTagTeamPopularity(t), 3);
        weights.push([t, Math.floor(weight)]);
    }
    return weights;
}

function getAddTagTeamWeights(superstars, style='standard') {  // add_tagteam_weights
    let weights = [];
    for (let s of superstars) {
        let talentPopularity = limitValueBetweenRange(s.popularity, 1, 10000);
        let weight;
        if (style !== 'bizarro') {
            weight = Math.pow(1000 / talentPopularity, 2);
        } else {
            weight = Math.exp(talentPopularity / 150);
        }
        weights.push([s, Math.floor(weight)]);
    }
    return weights;
}

function addTagteamPopularityCheck(weights, firstSuperstar, style='standard') {  // add_tagteam_popularity_check
    if (style === 'standard') {
        return Math.abs(weights[0].popularity - firstSuperstar.popularity) > 50 ?
            [weights[0], weights[1]] : [weights[0], weights[1] * 5];
    } else if (style === 'long-term') {
        return Math.abs(weights[0].popularity - firstSuperstar.popularity) < 100 ?
            [weights[0], weights[1]] : [weights[0], weights[1] * 5];
    } else {
        return [weights[0], weights[1]];
    }
}

async function getNegotiationWeights(talents, style='standard') {  // get_negotiation_weights
    let weights = [];
    for (let t of talents) {
        let weight = 1;
        for (let s of (await getRosterMemberSuperstars(t))) {
            weight += ((s.workrate - 1 + s.charisma - 1) * 15);

            if (s.hot) {
                weight += 40;
            }
            if (s.contractMonthsRemaining !== null) {
                weight *= 20;
            }
            if (s.fullTimeManager) {
                weight /= 3;
            }

            if (style === 'long-term') {
                let ageBiases = new Map([[25, 2], [32, 1.5], [38, 0.5], [45, 0.3]]);
                let ageBias = 0.3;
                for (let [key, value] of ageBiases) {
                    if (key > s.age) {
                        ageBias = value;
                        break;
                    }
                }
                weight *= ageBias;

                if (s.partTimer) {
                    weight /= 3;
                }
            }
        }
        weights.push([t, Math.round(weight)]);
    }
    return weights;
}

function getRetireChampionshipWeights(championships) {  // add_championship_weights
    let weights = [];
    let prestigeWeights = {'S': 1, 'A': 2, 'B': 3, 'C': 4};
    for (let c of championships) {
        let weight = Math.pow((prestigeWeights[c.prestige] || 1), 3);
        weights.push([c, Math.floor(weight)]);
    }
    return weights;
}

async function getRefereeWeights(rosterSuperstars, bookedParticipants) {
    let weights = [];
    for (let x of rosterSuperstars) {
        if (objectInArray(x, bookedParticipants)) {
            continue
        }
        let weight = 1;
        if (x.retired) {
            weight *= 10
        }
        if (x.gender === (await getRosterMemberSuperstars(bookedParticipants[0]))[0].gender) {
            weight *= 20
        }
        weights.push([x, Math.floor(weight)]);
    }
    return weights;
}

export {autoBookEventRequest, runSimulator, simulateCompetitors}